import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import Modal from '../components/Modal';
import CustomerRevenueReport from '../components/CustomerRevenueReport';
import PastDueStatements from '../components/PastDueStatements';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const normalized = dateStr.includes('T') ? dateStr : dateStr.includes(' ') ? dateStr.replace(' ', 'T') : dateStr + 'T00:00:00';
  return new Date(normalized.split('T')[0] + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

// Get month options for quick selection
function getMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({
      label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      start: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0],
      end: new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0],
    });
  }
  return options;
}

// Get year options
function getYearOptions() {
  const options = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 5; i++) {
    const year = currentYear - i;
    options.push({
      label: year.toString(),
      start: `${year}-01-01`,
      end: `${year}-12-31`,
    });
  }
  return options;
}

function getDefaultDates() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    period_start: start.toISOString().split('T')[0],
    period_end: end.toISOString().split('T')[0],
  };
}

// Every engineer assigned to at least one PO project, alphabetical, no duplicates
function getPoHistoryEngineers(projects) {
  const engineersById = new Map();
  projects.forEach(project => project.engineers.forEach(eng => engineersById.set(eng.id, eng)));
  return [...engineersById.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export default function Reports() {
  const [activeTab, setActiveTab] = useState('hours-summary');
  const [hoursSummaryData, setHoursSummaryData] = useState([]);
  const [hoursSummaryRange, setHoursSummaryRange] = useState(getDefaultDates());
  const [hoursEngineerFilter, setHoursEngineerFilter] = useState('');
  const [hoursCustomerFilter, setHoursCustomerFilter] = useState('');
  const [payrollData, setPayrollData] = useState([]);
  const [payrollHolidays, setPayrollHolidays] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [budgetCustomerFilter, setBudgetCustomerFilter] = useState('');
  const [budgetContactFilter, setBudgetContactFilter] = useState('');
  const [budgetEngineerFilter, setBudgetEngineerFilter] = useState('');
  const [invoicedData, setInvoicedData] = useState([]);
  const [contractHoursData, setContractHoursData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(getDefaultDates());
  const [invoicedDateRange, setInvoicedDateRange] = useState(getDefaultDates());
  const [invoicedCustomerFilter, setInvoicedCustomerFilter] = useState('');
  const [invoicedProjectFilter, setInvoicedProjectFilter] = useState('');
  const [invoicedEngineerFilter, setInvoicedEngineerFilter] = useState('');
  const [error, setError] = useState('');
  const [achModal, setAchModal] = useState(false);
  const [achDeliveryDate, setAchDeliveryDate] = useState('');
  const [achSelections, setAchSelections] = useState({});
  const [paidSelections, setPaidSelections] = useState({});
  const [markingPaid, setMarkingPaid] = useState(false);
  const [paidForPeriod, setPaidForPeriod] = useState([]);
  const [paidDetailData, setPaidDetailData] = useState([]);

  // Engineer payments state
  const [overpayments, setOverpayments] = useState([]);
  const [writeoffTarget, setWriteoffTarget] = useState(null);
  const [writeoffReason, setWriteoffReason] = useState('');
  const [engPayments, setEngPayments] = useState([]);
  const [engPayFilter, setEngPayFilter] = useState({ user_id: '', period_start: `${new Date().getFullYear()}-01-01`, period_end: '', payment_type: '' });
  const [engPayForm, setEngPayForm] = useState({ user_id: '', amount: '', payment_date: new Date().toISOString().split('T')[0], payment_type: 'advance', period_start: '', period_end: '', reference_number: '', payment_method: '', notes: '' });
  const [engPaySaving, setEngPaySaving] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear().toString());
  const [summary1099, setSummary1099] = useState([]);
  const [verificationData, setVerificationData] = useState(null);
  const [verificationEngineer, setVerificationEngineer] = useState('');
  const [verificationRange, setVerificationRange] = useState({ period_start: `${new Date().getFullYear()}-01-01`, period_end: new Date().toISOString().split('T')[0] });
  const [reconciliationData, setReconciliationData] = useState(null);
  const [reconciliationForm, setReconciliationForm] = useState({ user_id: '', period_start: `${new Date().getFullYear()}-01-01`, period_end: new Date().toISOString().split('T')[0] });
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [overdueData, setOverdueData] = useState([]);
  const [unclearedAdvances, setUnclearedAdvances] = useState([]);
  const [bankSplits, setBankSplits] = useState({});
  const [pendingAdjustments, setPendingAdjustments] = useState([]);
  const [adjForm, setAdjForm] = useState({ user_id: '', amount: '', reason: '' });
  const [showAdjForm, setShowAdjForm] = useState(false);
  const [adjSaving, setAdjSaving] = useState(false);
  const [payrollDetailEngineer, setPayrollDetailEngineer] = useState(null);

  // Profitability state
  const [profitData, setProfitData] = useState([]);
  const [profitActualPaid, setProfitActualPaid] = useState({});
  const [profitRange, setProfitRange] = useState({ period_start: `${new Date().getFullYear()}-01-01`, period_end: new Date().toISOString().split('T')[0] });
  const [profitCustomerFilter, setProfitCustomerFilter] = useState('');
  const [profitProjectFilter, setProfitProjectFilter] = useState('');
  const [profitEngineerFilter, setProfitEngineerFilter] = useState('');
  const [profitSummarySort, setProfitSummarySort] = useState({ key: 'profit', dir: 'desc' });
  const [profitDetailSort, setProfitDetailSort] = useState({ key: 'engineer_name', dir: 'asc' });

  // PO History state
  const [poHistoryProjects, setPoHistoryProjects] = useState([]);
  const [poHistoryProjectId, setPoHistoryProjectId] = useState('');
  const [poHistoryEngineerId, setPoHistoryEngineerId] = useState('');
  const [poHistoryData, setPoHistoryData] = useState(null);
  const [poHistoryLoading, setPoHistoryLoading] = useState(false);

  // Year-End Reports state
  const [yearEndYear, setYearEndYear] = useState(new Date().getFullYear().toString());
  const [yearEndData, setYearEndData] = useState(null);
  const [yearEndExpanded, setYearEndExpanded] = useState({});
  const [yearEndSort, setYearEndSort] = useState({});

  const monthOptions = getMonthOptions();
  const yearOptions = getYearOptions();

  useEffect(() => {
    if (activeTab === 'budget') {
      loadBudgetData();
    } else if (activeTab === 'contract-hours') {
      loadContractHoursData();
    } else if (activeTab === 'engineer-payments') {
      loadEngineers();
      loadEngPayments();
      loadPendingAdjustments();
      apiFetch(`/reports/overpayments?year=${new Date().getFullYear()}`).then(data => setOverpayments(data || [])).catch(() => {});
    } else if (activeTab === 'overdue') {
      loadOverdueInvoices();
    } else if (activeTab === 'profitability') {
      loadProfitability();
    } else if (activeTab === 'year-end') {
      loadYearEndData();
    } else if (activeTab === 'po-history') {
      apiFetch('/reports/po-history/projects').then(data => setPoHistoryProjects(data || [])).catch(err => setError(err.message));
    }
  }, [activeTab]);

  // Auto-load hours summary when tab is active and date range changes
  useEffect(() => {
    if (activeTab === 'hours-summary') {
      loadHoursSummary();
    }
  }, [activeTab, hoursSummaryRange.period_start, hoursSummaryRange.period_end]);

  const loadHoursSummary = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(
        `/reports/hours-summary?period_start=${hoursSummaryRange.period_start}&period_end=${hoursSummaryRange.period_end}`
      );
      setHoursSummaryData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPayrollData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(
        `/reports/payroll?period_start=${dateRange.period_start}&period_end=${dateRange.period_end}`
      );
      setPayrollData(response.data || []);
      setPayrollHolidays(response.holidays || []);
      setUnclearedAdvances(response.unclearedAdvances || []);
      setPendingAdjustments(response.pendingAdjustments || []);
      setPaidForPeriod(response.paidForPeriod || []);
      setPaidDetailData(response.paidData || []);
      setBankSplits(response.bankSplits || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBudgetData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/reports/project-budget');
      setBudgetData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoicedData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(
        `/reports/invoiced?period_start=${invoicedDateRange.period_start}&period_end=${invoicedDateRange.period_end}`
      );
      setInvoicedData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadContractHoursData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/reports/contract-hours');
      setContractHoursData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOverdueInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/reports/overdue-invoices');
      setOverdueData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadYearEndData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/reports/year-end?year=${yearEndYear}`);
      setYearEndData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProfitability = async (range) => {
    const r = range || profitRange;
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/reports/profitability?period_start=${r.period_start}&period_end=${r.period_end}`);
      setProfitData(data.rows || []);
      setProfitActualPaid(data.actualPaid || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleYearEndSection = (key) => {
    setYearEndExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const setYearEndSortFn = (section, field) => {
    setYearEndSort(prev => {
      const current = prev[section];
      if (current && current.field === field) {
        return { ...prev, [section]: { field, dir: current.dir === 'asc' ? 'desc' : 'asc' } };
      }
      return { ...prev, [section]: { field, dir: 'desc' } };
    });
  };

  const sortYearEndData = (data, section, defaultSort) => {
    const sort = yearEndSort[section] || defaultSort;
    if (!sort) return data;
    return [...data].sort((a, b) => {
      let av = a[sort.field], bv = b[sort.field];
      if (av == null) av = '';
      if (bv == null) bv = '';
      if (typeof av === 'string') {
        const cmp = av.localeCompare(bv);
        return sort.dir === 'asc' ? cmp : -cmp;
      }
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
  };

  const SortHeader = ({ section, field, children, style }) => {
    const sort = yearEndSort[section];
    const active = sort && sort.field === field;
    return (
      <th style={{ ...style, cursor: 'pointer', userSelect: 'none' }} onClick={() => setYearEndSortFn(section, field)}>
        {children} {active ? (sort.dir === 'asc' ? '▲' : '▼') : <span style={{ color: '#cbd5e1' }}>⇅</span>}
      </th>
    );
  };

  const loadEngineers = async () => {
    try {
      const users = await apiFetch('/users');
      setEngineers(users.filter(u => u.role === 'engineer'));
    } catch (e) {}
  };

  const handleWriteoff = async () => {
    if (!writeoffTarget || !writeoffReason.trim()) return;
    try {
      await apiFetch('/reports/overpayment-writeoff', {
        method: 'POST',
        body: { user_id: writeoffTarget.engineer_id, amount: writeoffTarget.total_overpaid, reason: writeoffReason.trim(), year: new Date().getFullYear() },
      });
      setOverpayments(overpayments.filter(op => op.engineer_id !== writeoffTarget.engineer_id));
      setWriteoffTarget(null);
      setWriteoffReason('');
    } catch (e) {
      alert('Failed to write off overpayment');
    }
  };

  const loadEngPayments = async (filters) => {
    const f = filters || engPayFilter;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.user_id) params.set('user_id', f.user_id);
      if (f.period_start) params.set('period_start', f.period_start);
      if (f.period_end) params.set('period_end', f.period_end);
      if (f.payment_type) params.set('payment_type', f.payment_type);
      const data = await apiFetch(`/engineer-payments?${params}`);
      setEngPayments(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingAdjustments = async () => {
    try {
      const data = await apiFetch('/payment-adjustments?status=pending');
      setPendingAdjustments(data);
    } catch (e) {}
  };

  const handleCreateAdjustment = async (e) => {
    e.preventDefault();
    if (!adjForm.user_id || !adjForm.amount || !adjForm.reason.trim()) {
      setError('Engineer, amount, and reason are required');
      return;
    }
    setAdjSaving(true);
    setError('');
    try {
      await apiFetch('/payment-adjustments', { method: 'POST', body: { user_id: adjForm.user_id, amount: parseFloat(adjForm.amount), reason: adjForm.reason.trim() } });
      setAdjForm({ user_id: '', amount: '', reason: '' });
      setShowAdjForm(false);
      loadPendingAdjustments();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdjSaving(false);
    }
  };

  const handleApplyAdjustment = async (adjId) => {
    if (!window.confirm('Apply this adjustment? This marks it as resolved.')) return;
    try {
      await apiFetch(`/payment-adjustments/${adjId}/apply`, { method: 'PUT', body: {} });
      loadPendingAdjustments();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleDeleteAdjustment = async (adjId) => {
    if (!window.confirm('Delete this pending adjustment?')) return;
    try {
      await apiFetch(`/payment-adjustments/${adjId}`, { method: 'DELETE' });
      loadPendingAdjustments();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const loadReconciliation = async () => {
    if (!reconciliationForm.user_id) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams(reconciliationForm);
      const data = await apiFetch(`/reports/engineer-reconciliation?${params}`);
      setReconciliationData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEngPayment = async (e) => {
    e.preventDefault();
    if (!engPayForm.user_id || !engPayForm.amount || !engPayForm.payment_date) {
      setError('Engineer, amount, and date are required');
      return;
    }
    setEngPaySaving(true);
    setError('');
    try {
      await apiFetch('/engineer-payments', { method: 'POST', body: { ...engPayForm, amount: parseFloat(engPayForm.amount) } });
      setEngPayForm({ ...engPayForm, amount: '', reference_number: '', notes: '' });
      loadEngPayments();
    } catch (e) {
      setError(e.message);
    } finally {
      setEngPaySaving(false);
    }
  };

  const handleDeleteEngPayment = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await apiFetch(`/engineer-payments/${id}`, { method: 'DELETE' });
      loadEngPayments();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleSaveEditPayment = async () => {
    if (!editingPayment) return;
    try {
      setEngPaySaving(true);
      await apiFetch(`/engineer-payments/${editingPayment.id}`, {
        method: 'PUT',
        body: {
          amount: parseFloat(editingPayment.amount),
          payment_date: editingPayment.payment_date,
          payment_type: editingPayment.payment_type,
          payment_method: editingPayment.payment_method || null,
          reference_number: editingPayment.reference_number || null,
          notes: editingPayment.notes || null,
          period_start: editingPayment.period_start || null,
          period_end: editingPayment.period_end || null
        }
      });
      setEditingPayment(null);
      loadEngPayments();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setEngPaySaving(false);
    }
  };

  const load1099Summary = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/engineer-payments/1099-summary?year=${taxYear}`);
      setSummary1099(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadVerification = async () => {
    if (!verificationEngineer) { setError('Select an engineer'); return; }
    setLoading(true);
    try {
      const data = await apiFetch(`/engineer-payments/verification/${verificationEngineer}?period_start=${verificationRange.period_start}&period_end=${verificationRange.period_end}`);
      setVerificationData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRunPayroll = (e) => {
    e.preventDefault();
    loadPayrollData();
  };

  const handleRunInvoiced = (e) => {
    e.preventDefault();
    loadInvoicedData();
  };

  const openAchModal = () => {
    // Default delivery date to 2 business days from now
    const date = new Date();
    date.setDate(date.getDate() + 2);
    // Skip weekends
    while (date.getDay() === 0 || date.getDay() === 6) {
      date.setDate(date.getDate() + 1);
    }
    setAchDeliveryDate(date.toISOString().split('T')[0]);
    // Initialize selections from payroll summary - include all engineers, even $0 net pay
    const selections = {};
    payrollSummary.forEach(row => {
      if (row.gross_pay > 0 || row.advance_deduction > 0) {
        selections[row.engineer_name] = {
          selected: row.total_pay > 0,
          amount: row.total_pay,
          defaultAmount: row.total_pay,
          grossPay: row.gross_pay,
          advanceDeduction: row.advance_deduction,
          fullyPaidByAdvance: row.total_pay <= 0 && row.advance_deduction > 0
        };
      }
    });
    setAchSelections(selections);
    setAchModal(true);
  };

  const handleAchExport = async () => {
    const selectedEngineers = Object.entries(achSelections)
      .filter(([, v]) => v.selected && v.amount > 0);
    if (selectedEngineers.length === 0) {
      alert('Please select at least one engineer with a payment amount.');
      return;
    }
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('tt_token');
      // Build overrides: engineer_name -> custom amount
      const overrides = {};
      selectedEngineers.forEach(([name, v]) => {
        overrides[name] = v.amount;
      });
      const url = `${API_BASE}/api/payroll/ach-export`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          period_start: dateRange.period_start,
          period_end: dateRange.period_end,
          delivery_date: achDeliveryDate,
          overrides
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Export failed');
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `ACH_Payroll_${achDeliveryDate.replace(/-/g, '')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      setAchModal(false);
    } catch (e) {
      alert('ACH Export Error: ' + e.message);
    }
  };

  const handleMarkAsPaid = async () => {
    const selected = payrollSummary.filter(r => paidSelections[r.engineer_name]);
    if (selected.length === 0) {
      alert('Please select at least one engineer to mark as paid.');
      return;
    }
    const names = selected.map(r => r.engineer_name).join(', ');
    if (!window.confirm(`Record payments for ${selected.length} engineer(s)?\n\n${names}`)) return;
    try {
      setMarkingPaid(true);
      for (const row of selected) {
        await apiFetch('/engineer-payments', {
          method: 'POST',
          body: {
            user_id: row.user_id,
            amount: row.total_pay,
            payment_date: new Date().toISOString().split('T')[0],
            payment_type: 'payroll',
            period_start: dateRange.period_start,
            period_end: dateRange.period_end,
            payment_method: 'ACH',
            reference_number: null,
            notes: `Payroll for ${dateRange.period_start} to ${dateRange.period_end}`
          }
        });
        // Mark timesheets as paid for this engineer/period
        // For delayed engineers, use their actual work period (shifted dates)
        const markStart = row.pay_period_start || dateRange.period_start;
        const markEnd = row.pay_period_end || dateRange.period_end;
        await apiFetch('/timesheets/mark-paid', {
          method: 'POST',
          body: {
            user_id: row.user_id,
            period_start: markStart,
            period_end: markEnd
          }
        });
        // Clear any uncleared advances for this engineer
        const engineerAdvances = unclearedAdvances.filter(a => a.user_id === row.user_id);
        for (const adv of engineerAdvances) {
          await apiFetch(`/engineer-payments/${adv.id}/clear`, {
            method: 'PUT',
            body: { cleared: true, cleared_payroll_period: `${dateRange.period_start} to ${dateRange.period_end}` }
          });
        }
        // Apply any pending adjustments for this engineer
        const engineerAdjustments = pendingAdjustments.filter(a => a.user_id === row.user_id);
        for (const adj of engineerAdjustments) {
          await apiFetch(`/payment-adjustments/${adj.id}/apply`, { method: 'PUT', body: {} });
        }
      }
      alert(`${selected.length} payment(s) recorded successfully.`);
      setPaidSelections({});
      loadPayrollData();
    } catch (e) {
      alert('Error recording payments: ' + e.message);
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleUndoPayment = async (row) => {
    if (!window.confirm(`Undo payment for ${row.engineer_name}?\n\nThis will delete the payment record and unmark their timesheets so they appear unpaid again.`)) return;
    try {
      await apiFetch(`/engineer-payments/${row.payment_id}`, { method: 'DELETE' });
      const unmarkStart = row.pay_period_start || dateRange.period_start;
      const unmarkEnd = row.pay_period_end || dateRange.period_end;
      await apiFetch('/timesheets/unmark-paid', {
        method: 'POST',
        body: {
          user_id: row.user_id,
          period_start: unmarkStart,
          period_end: unmarkEnd
        }
      });
      loadPayrollData();
    } catch (e) {
      alert('Error undoing payment: ' + e.message);
    }
  };

  const selectMonth = (option) => {
    setDateRange({ period_start: option.start, period_end: option.end });
  };

  const selectInvoicedPeriod = (option) => {
    setInvoicedDateRange({ period_start: option.start, period_end: option.end });
  };

  // Group advances by engineer
  const advancesByEngineer = unclearedAdvances.reduce((acc, adv) => {
    if (!acc[adv.engineer_name]) acc[adv.engineer_name] = { total: 0, items: [] };
    acc[adv.engineer_name].total += adv.amount;
    acc[adv.engineer_name].items.push(adv);
    return acc;
  }, {});

  // Group pending adjustments by engineer
  const adjustmentsByEngineer = pendingAdjustments.reduce((acc, adj) => {
    if (!acc[adj.engineer_name]) acc[adj.engineer_name] = { total: 0, items: [] };
    acc[adj.engineer_name].total += Math.abs(adj.amount);
    acc[adj.engineer_name].items.push(adj);
    return acc;
  }, {});

  // Group payroll by engineer for summary
  const payrollByEngineer = payrollData.reduce((acc, row) => {
    const key = row.engineer_name;
    if (!acc[key]) {
      acc[key] = { engineer_name: row.engineer_name, engineer_id: row.engineer_id, user_id: row.user_id, total_hours: 0, gross_pay: 0, total_pay: 0, holiday_hours: 0, holiday_pay: 0, advance_deduction: 0, pay_delay_months: row.pay_delay_months || 0, pay_period_label: row.pay_period_label || null, pay_period_start: row.pay_period_start || null, pay_period_end: row.pay_period_end || null };
    }
    if (row.is_holiday_pay) {
      acc[key].holiday_hours += row.total_hours || 0;
      acc[key].holiday_pay += row.total_pay || 0;
    } else {
      acc[key].total_hours += row.total_hours || 0;
    }
    acc[key].gross_pay += row.total_pay || 0;
    return acc;
  }, {});

  // Apply advance deductions and pending adjustments to summary
  for (const row of Object.values(payrollByEngineer)) {
    const advances = advancesByEngineer[row.engineer_name];
    const adjustments = adjustmentsByEngineer[row.engineer_name];
    row.advance_deduction = advances ? advances.total : 0;
    row.adjustment_deduction = adjustments ? adjustments.total : 0;
    row.total_pay = Math.max(0, row.gross_pay - row.advance_deduction - row.adjustment_deduction);
  }

  // Handle paid engineers: zero out amounts for those already paid, add missing ones
  const paidUserIds = new Set(paidForPeriod.map(p => p.user_id));
  for (const paid of paidForPeriod) {
    const existing = Object.values(payrollByEngineer).find(r => r.user_id === paid.user_id);
    if (existing) {
      existing.gross_pay = 0;
      existing.total_pay = 0;
      existing.total_hours = 0;
      existing.holiday_hours = 0;
      existing.holiday_pay = 0;
      existing.advance_deduction = 0;
      existing.adjustment_deduction = 0;
      existing.paid_amount = paid.amount;
      existing.payment_id = paid.payment_id;
    } else {
      payrollByEngineer[paid.engineer_name] = {
        engineer_name: paid.engineer_name,
        engineer_id: paid.engineer_id,
        user_id: paid.user_id,
        total_hours: 0,
        gross_pay: 0,
        total_pay: 0,
        holiday_hours: 0,
        holiday_pay: 0,
        advance_deduction: 0,
        adjustment_deduction: 0,
        paid_amount: paid.amount,
        payment_id: paid.payment_id,
        pay_delay_months: 0,
        pay_period_label: null,
        pay_period_start: null,
        pay_period_end: null
      };
    }
  }

  const payrollSummary = Object.values(payrollByEngineer);

  // Set of user_ids already paid for this period
  const alreadyPaidUserIds = new Set(paidForPeriod.map(p => p.user_id));

  const hasAnyAdvances = unclearedAdvances.length > 0;
  const hasAnyAdjustments = pendingAdjustments.length > 0;
  const totalAdvanceDeductions = payrollSummary.reduce((s, r) => s + r.advance_deduction, 0);
  const unpaidBilled = payrollData.filter(r => !paidUserIds.has(r.user_id)).reduce((s, r) => s + (r.total_billed || 0), 0);
  const paidBilled = paidDetailData.reduce((s, r) => s + (r.total_billed || 0), 0);
  const billedTotal = unpaidBilled + paidBilled;
  const payrollTotals = payrollSummary.reduce(
    (acc, row) => {
      const isPaid = alreadyPaidUserIds.has(row.user_id);
      const displayGross = isPaid && row.paid_amount ? row.paid_amount : (row.gross_pay || 0);
      return {
        hours: acc.hours + (row.total_hours || 0),
        holidayHours: acc.holidayHours + (row.holiday_hours || 0),
        grossPay: acc.grossPay + displayGross,
        pay: acc.pay + (row.total_pay || 0),
        billed: billedTotal,
        advanceDeductions: acc.advanceDeductions + (row.advance_deduction || 0),
        adjustmentDeductions: acc.adjustmentDeductions + (row.adjustment_deduction || 0),
      };
    },
    { hours: 0, holidayHours: 0, grossPay: 0, pay: 0, billed: billedTotal, advanceDeductions: 0, adjustmentDeductions: 0 }
  );
  const totalAdjustmentDeductions = payrollSummary.reduce((s, r) => s + (r.adjustment_deduction || 0), 0);
  payrollTotals.pay = Math.max(0, payrollTotals.grossPay - totalAdvanceDeductions - totalAdjustmentDeductions);

  const filteredBudgetData = budgetData.filter(row => {
    if (budgetCustomerFilter && row.customer_name !== budgetCustomerFilter) return false;
    if (budgetContactFilter && row.contact_name !== budgetContactFilter) return false;
    if (budgetEngineerFilter && !(row.engineers || '').includes(budgetEngineerFilter)) return false;
    return true;
  });

  const budgetTotals = filteredBudgetData.reduce(
    (acc, row) => ({
      po: acc.po + (row.po_amount || 0),
      billed: acc.billed + (row.amount_billed || 0),
      remaining: acc.remaining + (row.remaining || 0),
    }),
    { po: 0, billed: 0, remaining: 0 }
  );

  const filteredInvoicedData = invoicedData.filter(row => {
    if (invoicedCustomerFilter && row.customer_name !== invoicedCustomerFilter) return false;
    if (invoicedProjectFilter && row.project_name !== invoicedProjectFilter) return false;
    if (invoicedEngineerFilter && !(row.engineers || []).some(e => e.name === invoicedEngineerFilter)) return false;
    return true;
  });

  const invoicedCustomers = [...new Set(invoicedData.map(r => r.customer_name))].sort();
  const invoicedProjects = [...new Set(invoicedData.map(r => r.project_name))].sort();
  const invoicedEngineers = [...new Set(invoicedData.flatMap(r => (r.engineers || []).map(e => e.name)))].sort();

  const invoicedTotals = filteredInvoicedData.reduce(
    (acc, row) => ({
      count: acc.count + 1,
      total: acc.total + (row.total_amount || 0),
      paid: acc.paid + (row.amount_paid || 0),
      outstanding: acc.outstanding + ((row.total_amount || 0) - (row.amount_paid || 0)),
      engineerCost: acc.engineerCost + (row.engineer_cost || 0),
    }),
    { count: 0, total: 0, paid: 0, outstanding: 0, engineerCost: 0 }
  );

  return (
    <div>
      <div className="page-header no-print">
        <div>
          <div className="page-title">Reports</div>
          <div className="page-subtitle">Payroll, invoicing, and project budget reports</div>
        </div>
      </div>

      <div className="card no-print" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className={`btn ${activeTab === 'hours-summary' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('hours-summary')}
          >
            Hours Summary
          </button>
          <button
            className={`btn ${activeTab === 'payroll' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('payroll')}
          >
            Payroll Report
          </button>
          <button
            className={`btn ${activeTab === 'invoiced' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('invoiced')}
          >
            Invoiced Report
          </button>
          <button
            className={`btn ${activeTab === 'customer-revenue' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('customer-revenue')}
          >
            Customer Revenue
          </button>
          <button
            className={`btn ${activeTab === 'budget' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('budget')}
          >
            Project Budget Report
          </button>
          <button
            className={`btn ${activeTab === 'contract-hours' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('contract-hours')}
          >
            Contract Hours
          </button>
          <button
            className={`btn ${activeTab === 'engineer-payments' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('engineer-payments')}
          >
            Engineer Payments
          </button>
          <button
            className={`btn ${activeTab === 'overdue' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('overdue')}
          >
            Overdue Invoices
          </button>
          <button
            className={`btn ${activeTab === 'profitability' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('profitability')}
          >
            Profitability
          </button>
          <button
            className={`btn ${activeTab === 'po-history' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('po-history')}
          >
            PO History
          </button>
          <button
            className={`btn ${activeTab === 'year-end' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('year-end')}
            style={{ marginLeft: 'auto', borderColor: '#7c3aed', color: activeTab === 'year-end' ? undefined : '#7c3aed' }}
          >
            Year-End Reports
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error no-print">{error}</div>}

      {activeTab === 'hours-summary' && (() => {
        // Filter data
        const filtered = hoursSummaryData.filter(row =>
          (!hoursEngineerFilter || String(row.user_id) === hoursEngineerFilter) &&
          (!hoursCustomerFilter || String(row.customer_id) === hoursCustomerFilter)
        );

        // Group by engineer
        const byEngineer = {};
        filtered.forEach(row => {
          if (!byEngineer[row.engineer_name]) {
            byEngineer[row.engineer_name] = { rows: [], totalHours: 0, submittedHours: 0, draftHours: 0, submittedCount: 0, draftCount: 0, totalCount: 0 };
          }
          byEngineer[row.engineer_name].rows.push(row);
          byEngineer[row.engineer_name].totalHours += row.total_hours;
          byEngineer[row.engineer_name].submittedHours += row.submitted_hours;
          byEngineer[row.engineer_name].draftHours += row.draft_hours;
          byEngineer[row.engineer_name].submittedCount += row.submitted_count;
          byEngineer[row.engineer_name].draftCount += row.draft_count;
          byEngineer[row.engineer_name].totalCount += row.timesheet_count;
        });

        // Unique engineers and customers for filter dropdowns
        const uniqueEngineers = [...new Map(hoursSummaryData.map(r => [r.user_id, { id: r.user_id, name: r.engineer_name }])).values()].sort((a, b) => a.name.localeCompare(b.name));
        const uniqueCustomers = [...new Map(hoursSummaryData.map(r => [r.customer_id, { id: r.customer_id, name: r.customer_name }])).values()].sort((a, b) => a.name.localeCompare(b.name));

        const grandTotal = filtered.reduce((sum, r) => sum + r.total_hours, 0);

        return (
          <div className="card">
            <div className="card-title no-print">Hours Summary by Engineer</div>

            <div className="no-print" style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 12 }}>
                <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Quick Select Month:</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {monthOptions.slice(0, 6).map((opt, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`btn btn-sm ${hoursSummaryRange.period_start === opt.start && hoursSummaryRange.period_end === opt.end ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => { setHoursSummaryRange({ period_start: opt.start, period_end: opt.end }); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Period Start</label>
                  <input
                    className="form-input"
                    type="date"
                    value={hoursSummaryRange.period_start}
                    onChange={(e) => setHoursSummaryRange({ ...hoursSummaryRange, period_start: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Period End</label>
                  <input
                    className="form-input"
                    type="date"
                    value={hoursSummaryRange.period_end}
                    onChange={(e) => setHoursSummaryRange({ ...hoursSummaryRange, period_end: e.target.value })}
                  />
                </div>
                <button className="btn btn-primary" onClick={loadHoursSummary} disabled={loading}>
                  {loading ? 'Loading...' : 'Run Report'}
                </button>
              </div>
            </div>

            {hoursSummaryData.length > 0 && (
              <div className="no-print" style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Engineer:</span>
                  <select
                    className="form-select"
                    value={hoursEngineerFilter}
                    onChange={(e) => setHoursEngineerFilter(e.target.value)}
                    style={{ width: 'auto', minWidth: 180 }}
                  >
                    <option value="">All Engineers</option>
                    {uniqueEngineers.map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Customer:</span>
                  <select
                    className="form-select"
                    value={hoursCustomerFilter}
                    onChange={(e) => setHoursCustomerFilter(e.target.value)}
                    style={{ width: 'auto', minWidth: 180 }}
                  >
                    <option value="">All Customers</option>
                    {uniqueCustomers.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {filtered.length === 0 ? (
              <div className="empty-state no-print">
                <h3>No data</h3>
                <p>{hoursSummaryData.length > 0 ? 'Try adjusting your filters.' : 'Select a month and run the report.'}</p>
              </div>
            ) : (
              <>
                <div className="print-only" style={{ marginBottom: 20, textAlign: 'center' }}>
                  <h1 style={{ margin: 0, fontSize: 24 }}>Hours Summary by Engineer</h1>
                  <p style={{ margin: '8px 0 0', color: '#666' }}>
                    Period: {formatDate(hoursSummaryRange.period_start)} - {formatDate(hoursSummaryRange.period_end)}
                  </p>
                </div>

                {Object.entries(byEngineer).map(([engineerName, group]) => (
                  <div key={engineerName} style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                      <h3 style={{ fontSize: 16, margin: 0 }}>{engineerName}</h3>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, fontSize: 15 }}>
                          {group.totalHours.toFixed(2)} hrs
                        </span>
                        <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
                          ({group.totalCount} timesheets)
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 12 }}>
                      <span style={{ color: '#16a34a' }}>Submitted: {group.submittedCount} timesheets / {group.submittedHours.toFixed(2)} hrs</span>
                      {group.draftCount > 0 && (
                        <span style={{ color: '#d97706' }}>Draft: {group.draftCount} timesheets / {group.draftHours.toFixed(2)} hrs</span>
                      )}
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Project</th>
                            <th>Customer</th>
                            <th>Type</th>
                            <th style={{ textAlign: 'center' }}>Submitted</th>
                            <th style={{ textAlign: 'right' }}>Submitted Hrs</th>
                            <th style={{ textAlign: 'center' }}>Draft</th>
                            <th style={{ textAlign: 'right' }}>Draft Hrs</th>
                            <th style={{ textAlign: 'center' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Total Hrs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map(row => (
                            <tr key={row.project_id}>
                              <td><strong>{row.project_name}</strong></td>
                              <td>{row.customer_name}</td>
                              <td>
                                <span className={`badge ${row.project_type === 'fixed_price' || row.project_type === 'piece_rate' ? 'badge-fixed' : 'badge-hourly'}`} style={{ fontSize: 11 }}>
                                  {row.project_type === 'piece_rate' ? 'Piece Rate' : row.project_type === 'fixed_price' ? 'Fixed' : 'Hourly'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>{row.submitted_count}</td>
                              <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{row.submitted_hours.toFixed(2)}</td>
                              <td style={{ textAlign: 'center', color: row.draft_count > 0 ? '#d97706' : undefined }}>{row.draft_count}</td>
                              <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: row.draft_hours > 0 ? '#d97706' : undefined }}>{row.draft_hours.toFixed(2)}</td>
                              <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.timesheet_count}</td>
                              <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, textAlign: 'right' }}>{row.total_hours.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

                <div style={{ borderTop: '2px solid var(--border)', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>
                    Grand Total ({Object.keys(byEngineer).length} engineer{Object.keys(byEngineer).length !== 1 ? 's' : ''})
                  </span>
                  <span style={{ fontFamily: 'DM Mono, monospace', fontWeight: 700, fontSize: 18 }}>
                    {grandTotal.toFixed(2)} hrs
                  </span>
                </div>

                <div className="no-print" style={{ marginTop: 16 }}>
                  <button className="btn btn-secondary" onClick={() => window.print()}>Print Report</button>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {activeTab === 'payroll' && (
        <div className="card">
          <div className="card-title no-print">Payroll Report</div>

          <form onSubmit={handleRunPayroll} className="no-print" style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 12 }}>
              <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Quick Select Month:</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {monthOptions.slice(0, 6).map((opt, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`btn btn-sm ${dateRange.period_start === opt.start ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => selectMonth(opt)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Period Start</label>
                <input
                  className="form-input"
                  type="date"
                  value={dateRange.period_start}
                  onChange={(e) => setDateRange({ ...dateRange, period_start: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Period End</label>
                <input
                  className="form-input"
                  type="date"
                  value={dateRange.period_end}
                  onChange={(e) => setDateRange({ ...dateRange, period_end: e.target.value })}
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? 'Loading...' : 'Run Report'}
              </button>
              {(payrollData.length > 0 || paidForPeriod.length > 0) && (
                <>
                  <button className="btn btn-secondary" type="button" onClick={() => window.print()}>
                    Print Report
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={openAchModal}>
                    Generate ACH File
                  </button>
                </>
              )}
            </div>
          </form>

          {payrollData.length === 0 && paidForPeriod.length === 0 ? (
            <div className="empty-state no-print">
              <h3>No data</h3>
              <p>Select a date range and run the report to see payroll data.</p>
            </div>
          ) : (
            <>
              {/* Print Header */}
              <div className="print-only" style={{ marginBottom: 20, textAlign: 'center' }}>
                <h1 style={{ margin: 0, fontSize: 24 }}>Engineer Payroll Report</h1>
                <p style={{ margin: '8px 0 0', color: '#666' }}>
                  Period: {formatDate(dateRange.period_start)} - {formatDate(dateRange.period_end)}
                </p>
              </div>

              {/* Holidays included in this period */}
              {payrollHolidays.length > 0 && (
                <div style={{ marginBottom: 16, padding: '12px 16px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4, color: '#1e40af' }}>Holidays in Period</div>
                  <div style={{ fontSize: 14, color: '#3b82f6' }}>
                    {payrollHolidays.map((h, i) => (
                      <span key={h.id}>
                        {h.name} ({formatDate(h.date)}, {h.hours}hrs){i < payrollHolidays.length - 1 ? ' • ' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary by Engineer (for printing) */}
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 16, marginBottom: 12 }}>Payment Summary by Engineer</h3>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th className="no-print" style={{ width: 30 }}>
                          <input type="checkbox"
                            checked={payrollSummary.length > 0 && payrollSummary.filter(r => (r.total_pay > 0 || r.advance_deduction > 0) && !alreadyPaidUserIds.has(r.user_id)).every(r => paidSelections[r.engineer_name])}
                            onChange={(e) => {
                              const next = {};
                              if (e.target.checked) payrollSummary.forEach(r => { if ((r.total_pay > 0 || r.advance_deduction > 0) && !alreadyPaidUserIds.has(r.user_id)) next[r.engineer_name] = true; });
                              setPaidSelections(next);
                            }}
                          />
                        </th>
                        <th>Engineer</th>
                        <th>Engineer ID</th>
                        <th>Work Hours</th>
                        {payrollHolidays.length > 0 && <th>Holiday Hours</th>}
                        <th style={{ textAlign: 'right' }}>Gross Pay</th>
                        {hasAnyAdvances && <th style={{ textAlign: 'right' }}>Advances</th>}
                        {hasAnyAdjustments && <th style={{ textAlign: 'right' }}>Adjustments</th>}
                        <th style={{ textAlign: 'right' }}>Net Pay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollSummary.map((row, idx) => {
                        const isPaid = alreadyPaidUserIds.has(row.user_id);
                        return (
                        <tr key={idx} style={isPaid ? { opacity: 0.5 } : undefined}>
                          <td className="no-print">
                            <input type="checkbox"
                              checked={!!paidSelections[row.engineer_name]}
                              disabled={(row.total_pay <= 0 && row.advance_deduction <= 0) || isPaid}
                              onChange={(e) => setPaidSelections({ ...paidSelections, [row.engineer_name]: e.target.checked })}
                            />
                          </td>
                          <td>
                            <strong style={{ cursor: 'pointer', color: 'var(--primary)', textDecoration: 'underline' }} onClick={() => setPayrollDetailEngineer(row.engineer_name)}>{row.engineer_name}</strong>
                            {isPaid && (
                              <>
                                <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '2px 6px', borderRadius: 4 }}>PAID</span>
                                <button
                                  className="no-print"
                                  onClick={() => handleUndoPayment(row)}
                                  style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer' }}
                                >Undo</button>
                              </>
                            )}
                            {row.pay_delay_months > 0 && (
                              <div style={{ fontSize: 10, color: '#1e40af', marginTop: 2 }}>
                                {row.pay_period_label}
                              </div>
                            )}
                          </td>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{row.engineer_id || '-'}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>{(row.total_hours || 0).toFixed(2)}</td>
                          {payrollHolidays.length > 0 && (
                            <td style={{ fontFamily: 'DM Mono, monospace', color: row.holiday_hours > 0 ? '#1e40af' : '#94a3b8' }}>
                              {row.holiday_hours > 0 ? `${row.holiday_hours.toFixed(2)}` : '—'}
                            </td>
                          )}
                          <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>
                            {isPaid && row.paid_amount ? formatCurrency(row.paid_amount) : formatCurrency(row.gross_pay)}
                          </td>
                          {hasAnyAdvances && (
                            <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: row.advance_deduction > 0 ? '#dc2626' : '#94a3b8' }}>
                              {row.advance_deduction > 0 ? `(${formatCurrency(row.advance_deduction)})` : '—'}
                            </td>
                          )}
                          {hasAnyAdjustments && (
                            <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: row.adjustment_deduction > 0 ? '#f59e0b' : '#94a3b8' }}>
                              {row.adjustment_deduction > 0 ? `(${formatCurrency(row.adjustment_deduction)})` : '—'}
                            </td>
                          )}
                          <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, textAlign: 'right', color: isPaid ? '#64748b' : '#16a34a' }}>
                            {isPaid && row.paid_amount ? formatCurrency(row.paid_amount) : formatCurrency(row.total_pay)}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                        <td className="no-print"></td>
                        <td colSpan={2}>Totals</td>
                        <td style={{ fontFamily: 'DM Mono, monospace' }}>{payrollTotals.hours.toFixed(2)}</td>
                        {payrollHolidays.length > 0 && <td style={{ fontFamily: 'DM Mono, monospace' }}>{payrollTotals.holidayHours.toFixed(2)}</td>}
                        <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{formatCurrency(payrollTotals.grossPay)}</td>
                        {hasAnyAdvances && <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: '#dc2626' }}>({formatCurrency(payrollTotals.advanceDeductions)})</td>}
                        {hasAnyAdjustments && <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: '#f59e0b' }}>({formatCurrency(payrollTotals.adjustmentDeductions)})</td>}
                        <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: '#16a34a' }}>{formatCurrency(payrollTotals.pay)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {payrollDetailEngineer && (() => {
                  const unpaidDetails = payrollData.filter(r => r.engineer_name === payrollDetailEngineer);
                  const paidDetails = paidDetailData.filter(r => r.engineer_name === payrollDetailEngineer);
                  const details = [...unpaidDetails, ...paidDetails];
                  const summary = payrollSummary.find(r => r.engineer_name === payrollDetailEngineer);
                  const paidRecord = paidForPeriod.find(p => p.engineer_name === payrollDetailEngineer);
                  return (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setPayrollDetailEngineer(null)}>
                      <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 24, maxWidth: 800, width: '90%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                          <div>
                            <h3 style={{ margin: 0, fontSize: 18 }}>Pay Breakdown: {payrollDetailEngineer}</h3>
                            {paidRecord && (
                              <div style={{ fontSize: 13, color: '#16a34a', marginTop: 4 }}>
                                Paid {formatCurrency(paidRecord.amount)} on {formatDate(paidRecord.payment_date)}
                              </div>
                            )}
                          </div>
                          <button onClick={() => setPayrollDetailEngineer(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px 8px' }}>&times;</button>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--border)' }}>
                              <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 13 }}>Project</th>
                              <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 13 }}>Type</th>
                              <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 13 }}>Reg Hrs</th>
                              <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 13 }}>Rate</th>
                              <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 13 }}>Reg Pay</th>
                              <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 13 }}>OT Hrs</th>
                              <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 13 }}>OT Rate</th>
                              <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 13 }}>OT Pay</th>
                              <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 13, fontWeight: 700 }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.map((d, i) => {
                              const regHrs = d.regular_hours || d.total_hours || 0;
                              const otHrs = d.ot_hours || 0;
                              const payRate = d.pay_rate || 0;
                              const otPayRate = d.ot_pay_rate || 0;
                              const regPay = d.pay_type === 'hourly' ? regHrs * payRate : 0;
                              const otPay = d.pay_type === 'hourly' ? otHrs * otPayRate : 0;
                              const isFixed = d.pay_type !== 'hourly';
                              return (
                                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                                  <td style={{ padding: '8px 6px', fontSize: 13 }}>{d.project_name}</td>
                                  <td style={{ padding: '8px 6px', fontSize: 11, color: 'var(--text-secondary)' }}>
                                    {d.is_holiday_pay ? 'Holiday' : d.pay_type === 'piece_rate' ? 'Piece Rate' : d.pay_type === 'fixed_monthly' ? 'Fixed Mo.' : d.pay_type === 'fixed_price' ? 'Fixed' : 'Hourly'}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '8px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                                    {isFixed ? (d.total_hours || 0).toFixed(2) : regHrs.toFixed(2)}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '8px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                                    {isFixed ? '—' : formatCurrency(payRate)}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '8px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                                    {isFixed ? '—' : formatCurrency(regPay)}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '8px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13, color: otHrs > 0 ? '#ea580c' : 'var(--text-secondary)' }}>
                                    {otHrs > 0 ? otHrs.toFixed(2) : '—'}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '8px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13, color: otHrs > 0 ? '#ea580c' : 'var(--text-secondary)' }}>
                                    {otHrs > 0 ? formatCurrency(otPayRate) : '—'}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '8px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13, color: otHrs > 0 ? '#ea580c' : 'var(--text-secondary)' }}>
                                    {otHrs > 0 ? formatCurrency(otPay) : '—'}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '8px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13, fontWeight: 600 }}>
                                    {formatCurrency(d.total_pay || 0)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                              <td colSpan={2} style={{ padding: '10px 6px', fontSize: 14 }}>Total</td>
                              <td style={{ textAlign: 'right', padding: '10px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                                {details.reduce((s, d) => s + (d.regular_hours || d.total_hours || 0), 0).toFixed(2)}
                              </td>
                              <td></td>
                              <td style={{ textAlign: 'right', padding: '10px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                                {formatCurrency(details.filter(d => d.pay_type === 'hourly').reduce((s, d) => s + ((d.regular_hours || d.total_hours || 0) * (d.pay_rate || 0)), 0))}
                              </td>
                              <td style={{ textAlign: 'right', padding: '10px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13, color: '#ea580c' }}>
                                {details.reduce((s, d) => s + (d.ot_hours || 0), 0).toFixed(2)}
                              </td>
                              <td></td>
                              <td style={{ textAlign: 'right', padding: '10px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13, color: '#ea580c' }}>
                                {formatCurrency(details.filter(d => d.pay_type === 'hourly').reduce((s, d) => s + ((d.ot_hours || 0) * (d.ot_pay_rate || 0)), 0))}
                              </td>
                              <td style={{ textAlign: 'right', padding: '10px 6px', fontFamily: 'DM Mono, monospace', fontSize: 14, color: '#16a34a' }}>
                                {formatCurrency(details.reduce((s, d) => s + (d.total_pay || 0), 0))}
                              </td>
                            </tr>
                            {summary && summary.advance_deduction > 0 && (
                              <tr>
                                <td colSpan={8} style={{ textAlign: 'right', padding: '6px 6px', fontSize: 13, color: '#dc2626' }}>Advance Deduction:</td>
                                <td style={{ textAlign: 'right', padding: '6px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13, color: '#dc2626' }}>({formatCurrency(summary.advance_deduction)})</td>
                              </tr>
                            )}
                            {summary && summary.adjustment_deduction > 0 && (
                              <tr>
                                <td colSpan={8} style={{ textAlign: 'right', padding: '6px 6px', fontSize: 13, color: '#f59e0b' }}>Payment Adjustment:</td>
                                <td style={{ textAlign: 'right', padding: '6px 6px', fontFamily: 'DM Mono, monospace', fontSize: 13, color: '#f59e0b' }}>({formatCurrency(summary.adjustment_deduction)})</td>
                              </tr>
                            )}
                            {summary && (summary.advance_deduction > 0 || summary.adjustment_deduction > 0) && (
                              <tr style={{ borderTop: '1px solid var(--border)' }}>
                                <td colSpan={8} style={{ textAlign: 'right', padding: '8px 6px', fontSize: 14, fontWeight: 700 }}>Net Pay:</td>
                                <td style={{ textAlign: 'right', padding: '8px 6px', fontFamily: 'DM Mono, monospace', fontSize: 14, fontWeight: 700, color: '#16a34a' }}>{formatCurrency(summary.total_pay)}</td>
                              </tr>
                            )}
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {Object.values(paidSelections).some(v => v) && (() => {
                  const selectedEngineers = payrollSummary.filter(r => paidSelections[r.engineer_name]);
                  const selectedTotal = selectedEngineers.reduce((sum, r) => sum + r.total_pay, 0);
                  const hasSplits = selectedEngineers.some(r => bankSplits[r.user_id]);
                  return (
                  <div className="no-print" style={{ marginTop: 12 }}>
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: hasSplits ? 10 : 0 }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>
                          Total Payout ({selectedEngineers.length} engineer{selectedEngineers.length !== 1 ? 's' : ''}):
                        </span>
                        <span style={{ fontFamily: 'DM Mono, monospace', fontWeight: 700, fontSize: 18, color: '#16a34a' }}>
                          {formatCurrency(selectedTotal)}
                        </span>
                      </div>
                      {hasSplits && (
                        <div style={{ borderTop: '1px solid #bbf7d0', paddingTop: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Split Deposit Breakdown:</div>
                          {selectedEngineers.filter(r => bankSplits[r.user_id]).map(r => {
                            const split = bankSplits[r.user_id];
                            const amt1 = r.total_pay * (split.pct_1 / 100);
                            const amt2 = r.total_pay * (split.pct_2 / 100);
                            return (
                              <div key={r.user_id} style={{ fontSize: 13, marginBottom: 6, padding: '6px 10px', background: '#fff', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                <strong>{r.engineer_name}</strong>
                                <span style={{ color: '#64748b', marginLeft: 8 }}>({formatCurrency(r.total_pay)} total)</span>
                                <div style={{ display: 'flex', gap: 16, marginTop: 4, fontFamily: 'DM Mono, monospace', fontSize: 12 }}>
                                  <span>Acct 1 (****{split.account_1_last4} {split.account_1_type}): {split.pct_1}% = {formatCurrency(amt1)}</span>
                                  <span>Acct 2 (****{split.account_2_last4} {split.account_2_type}): {split.pct_2}% = {formatCurrency(amt2)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <button
                        className="btn btn-primary"
                        onClick={handleMarkAsPaid}
                        disabled={markingPaid}
                      >
                        {markingPaid ? 'Recording...' : `Mark ${selectedEngineers.length} Selected as Paid`}
                      </button>
                      <button className="btn btn-secondary" onClick={() => setPaidSelections({})}>Clear Selection</button>
                    </div>
                  </div>
                  );
                })()}
              </div>

              {/* Detailed Breakdown */}
              <h3 style={{ fontSize: 16, marginBottom: 12 }}>Detailed Breakdown by Project</h3>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Engineer</th>
                      <th>Engineer ID</th>
                      <th>Project</th>
                      <th>PO #</th>
                      <th>Hours</th>
                      <th>Pay Rate</th>
                      <th>Total Pay</th>
                      <th className="no-print">Bill Rate</th>
                      <th className="no-print">Total Billed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...payrollData, ...paidDetailData].map((row, idx) => {
                      const isFixed = row.pay_type === 'fixed_price';
                      const isPieceRate = row.pay_type === 'piece_rate';
                      const isFixedMonthly = row.pay_type === 'fixed_monthly';
                      const isHoliday = row.is_holiday_pay;
                      const isPaidRow = alreadyPaidUserIds.has(row.user_id);
                      const rowBg = isHoliday ? { background: '#eff6ff' } : (isFixed || isFixedMonthly || isPieceRate) ? { background: '#fefce8' } : undefined;
                      return (
                        <tr key={idx} style={{ ...rowBg, ...(isPaidRow ? { opacity: 0.5 } : {}) }}>
                          <td><strong>{row.engineer_name}</strong></td>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{row.engineer_id || '-'}</td>
                          <td>
                            {isHoliday ? (
                              <span style={{ color: '#1e40af', fontWeight: 500 }}>Holiday Pay</span>
                            ) : (
                              <>
                                {row.project_name}
                                {isFixed && <span style={{ marginLeft: 6, fontSize: 11, color: '#92400e', fontWeight: 500 }}>(Fixed Price)</span>}
                                {isPieceRate && <span style={{ marginLeft: 6, fontSize: 11, color: '#92400e', fontWeight: 500 }}>(Piece Rate)</span>}
                                {isFixedMonthly && <span style={{ marginLeft: 6, fontSize: 11, color: '#92400e', fontWeight: 500 }}>(Monthly)</span>}
                              </>
                            )}
                          </td>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, color: isHoliday ? '#3b82f6' : undefined }}>
                            {row.po_number || '-'}
                          </td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>
                            {isFixed ? `${row.percentage || 0}%` : (row.total_hours || 0).toFixed(2)}
                          </td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>
                            {isFixed ? 'Fixed' : isFixedMonthly ? 'Monthly' : `${formatCurrency(row.pay_rate)}/hr`}
                          </td>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{formatCurrency(row.total_pay)}</td>
                          <td className="no-print" style={{ fontFamily: 'DM Mono, monospace' }}>{(isHoliday || isFixed || isFixedMonthly) ? '—' : `${formatCurrency(row.bill_rate)}/hr`}</td>
                          <td className="no-print" style={{ fontFamily: 'DM Mono, monospace' }}>{(isHoliday || isFixed || isFixedMonthly) ? '—' : formatCurrency(row.total_billed)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                      <td colSpan={4}>Totals</td>
                      <td style={{ fontFamily: 'DM Mono, monospace' }}>{payrollTotals.hours.toFixed(2)}</td>
                      <td></td>
                      <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(payrollTotals.pay)}</td>
                      <td className="no-print"></td>
                      <td className="no-print" style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(payrollTotals.billed)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="no-print" style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div className="stat-card">
                  <div className="stat-label">Total Hours</div>
                  <div className="stat-value">{payrollTotals.hours.toFixed(1)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Gross Pay</div>
                  <div className="stat-value" style={{ fontSize: 22 }}>{formatCurrency(payrollTotals.grossPay)}</div>
                </div>
                {hasAnyAdvances && (
                  <div className="stat-card" style={{ borderColor: '#fbbf24' }}>
                    <div className="stat-label">Advance Deductions</div>
                    <div className="stat-value" style={{ fontSize: 22, color: '#dc2626' }}>({formatCurrency(payrollTotals.advanceDeductions)})</div>
                  </div>
                )}
                <div className="stat-card">
                  <div className="stat-label">Net Pay</div>
                  <div className="stat-value" style={{ fontSize: 22, color: '#16a34a' }}>{formatCurrency(payrollTotals.pay)}</div>
                </div>
                <div className="stat-card accent">
                  <div className="stat-label">Total Billable</div>
                  <div className="stat-value" style={{ fontSize: 22 }}>{formatCurrency(payrollTotals.billed)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Gross Margin</div>
                  <div className="stat-value" style={{ fontSize: 22, color: 'var(--success)' }}>
                    {formatCurrency(payrollTotals.billed - payrollTotals.pay)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'invoiced' && (
        <div className="card">
          <div className="card-title no-print">Invoiced Report</div>

          <form onSubmit={handleRunInvoiced} className="no-print" style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 12 }}>
              <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Quick Select:</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                {monthOptions.slice(0, 6).map((opt, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`btn btn-sm ${invoicedDateRange.period_start === opt.start && invoicedDateRange.period_end === opt.end ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => selectInvoicedPeriod(opt)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {yearOptions.map((opt, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`btn btn-sm ${invoicedDateRange.period_start === opt.start && invoicedDateRange.period_end === opt.end ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => selectInvoicedPeriod(opt)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Period Start</label>
                <input
                  className="form-input"
                  type="date"
                  value={invoicedDateRange.period_start}
                  onChange={(e) => setInvoicedDateRange({ ...invoicedDateRange, period_start: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Period End</label>
                <input
                  className="form-input"
                  type="date"
                  value={invoicedDateRange.period_end}
                  onChange={(e) => setInvoicedDateRange({ ...invoicedDateRange, period_end: e.target.value })}
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? 'Loading...' : 'Run Report'}
              </button>
              {invoicedData.length > 0 && (
                <button className="btn btn-secondary" type="button" onClick={() => window.print()}>
                  Print Report
                </button>
              )}
            </div>
          </form>

          {invoicedData.length === 0 ? (
            <div className="empty-state no-print">
              <h3>No data</h3>
              <p>Select a date range and run the report to see invoiced data.</p>
            </div>
          ) : (
            <>
              {/* Filters */}
              <div className="no-print" style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
                  <label className="form-label">Customer</label>
                  <select className="form-input" value={invoicedCustomerFilter} onChange={(e) => setInvoicedCustomerFilter(e.target.value)}>
                    <option value="">All Customers</option>
                    {invoicedCustomers.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
                  <label className="form-label">Project</label>
                  <select className="form-input" value={invoicedProjectFilter} onChange={(e) => setInvoicedProjectFilter(e.target.value)}>
                    <option value="">All Projects</option>
                    {invoicedProjects.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
                  <label className="form-label">Engineer</label>
                  <select className="form-input" value={invoicedEngineerFilter} onChange={(e) => setInvoicedEngineerFilter(e.target.value)}>
                    <option value="">All Engineers</option>
                    {invoicedEngineers.map(eng => <option key={eng} value={eng}>{eng}</option>)}
                  </select>
                </div>
              </div>

              {/* Print Header */}
              <div className="print-only" style={{ marginBottom: 20, textAlign: 'center' }}>
                <h1 style={{ margin: 0, fontSize: 24 }}>Invoiced Report</h1>
                <p style={{ margin: '8px 0 0', color: '#666' }}>
                  Period: {formatDate(invoicedDateRange.period_start)} - {formatDate(invoicedDateRange.period_end)}
                </p>
              </div>

              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Project</th>
                      <th>PO #</th>
                      <th>Hours</th>
                      <th>Amount</th>
                      <th>Engineer Cost</th>
                      <th>Paid</th>
                      <th>Outstanding</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoicedData.map((row) => {
                      const outstanding = (row.total_amount || 0) - (row.amount_paid || 0);
                      return (
                        <tr key={row.id}>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{row.invoice_number}</td>
                          <td style={{ fontSize: 13 }}>{formatDate(row.created_at)}</td>
                          <td><strong>{row.customer_name}</strong></td>
                          <td>{row.project_name}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{row.po_number || '-'}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>{(row.total_hours || 0).toFixed(2)}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(row.total_amount)}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace', color: '#7c3aed' }}
                            title={(row.engineers || []).map(e => `${e.name}: ${formatCurrency(e.cost)}`).join('\n')}
                          >
                            {formatCurrency(row.engineer_cost)}
                          </td>
                          <td style={{ fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(row.amount_paid)}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, color: outstanding > 0 ? '#dc2626' : '#16a34a' }}>
                            {formatCurrency(outstanding)}
                          </td>
                          <td>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              background: row.status === 'paid' ? '#d1fae5' : row.status === 'voided' ? '#f3f4f6' : '#fef3c7',
                              color: row.status === 'paid' ? '#065f46' : row.status === 'voided' ? '#6b7280' : '#92400e',
                            }}>
                              {row.status || 'unpaid'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                      <td colSpan={6}>Totals ({invoicedTotals.count} invoices)</td>
                      <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(invoicedTotals.total)}</td>
                      <td style={{ fontFamily: 'DM Mono, monospace', color: '#7c3aed' }}>{formatCurrency(invoicedTotals.engineerCost)}</td>
                      <td style={{ fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(invoicedTotals.paid)}</td>
                      <td style={{ fontFamily: 'DM Mono, monospace', color: invoicedTotals.outstanding > 0 ? '#dc2626' : '#16a34a' }}>
                        {formatCurrency(invoicedTotals.outstanding)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="no-print" style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div className="stat-card">
                  <div className="stat-label">Invoices Created</div>
                  <div className="stat-value">{invoicedTotals.count}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Invoiced</div>
                  <div className="stat-value" style={{ fontSize: 22 }}>{formatCurrency(invoicedTotals.total)}</div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #7c3aed' }}>
                  <div className="stat-label">Engineer Cost</div>
                  <div className="stat-value" style={{ fontSize: 22, color: '#7c3aed' }}>{formatCurrency(invoicedTotals.engineerCost)}</div>
                </div>
                <div className="stat-card accent">
                  <div className="stat-label">Gross Margin</div>
                  <div className="stat-value" style={{ fontSize: 22, color: 'var(--success)' }}>{formatCurrency(invoicedTotals.total - invoicedTotals.engineerCost)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Outstanding</div>
                  <div className="stat-value" style={{ fontSize: 22, color: invoicedTotals.outstanding > 0 ? 'var(--danger)' : 'var(--success)' }}>
                    {formatCurrency(invoicedTotals.outstanding)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'customer-revenue' && <CustomerRevenueReport />}

      {activeTab === 'budget' && (
        <div className="card">
          <div className="card-title">Project Budget Report</div>

          {loading ? (
            <div style={{ padding: 40, color: '#94a3b8', textAlign: 'center' }}>Loading...</div>
          ) : budgetData.length === 0 ? (
            <div className="empty-state">
              <h3>No projects</h3>
              <p>Create projects to see budget data.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Customer:</span>
                  <select className="form-select" value={budgetCustomerFilter} onChange={(e) => setBudgetCustomerFilter(e.target.value)} style={{ width: 'auto', minWidth: 150 }}>
                    <option value="">All</option>
                    {[...new Set(budgetData.map(r => r.customer_name))].sort().map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Contact:</span>
                  <select className="form-select" value={budgetContactFilter} onChange={(e) => setBudgetContactFilter(e.target.value)} style={{ width: 'auto', minWidth: 150 }}>
                    <option value="">All</option>
                    {[...new Set(budgetData.map(r => r.contact_name).filter(Boolean))].sort().map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Engineer:</span>
                  <select className="form-select" value={budgetEngineerFilter} onChange={(e) => setBudgetEngineerFilter(e.target.value)} style={{ width: 'auto', minWidth: 150 }}>
                    <option value="">All</option>
                    {[...new Set(budgetData.flatMap(r => (r.engineers || '').split(',').map(e => e.trim())).filter(Boolean))].sort().map(e => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </select>
                </div>
                <span style={{ fontSize: 13, color: '#94a3b8', marginLeft: 'auto', alignSelf: 'center' }}>
                  {filteredBudgetData.length} of {budgetData.length} projects
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={async () => {
                    const recipient = window.prompt('Email report to:', '');
                    if (!recipient) return;
                    try {
                      const result = await apiFetch('/reports/project-budget/email', {
                        method: 'POST',
                        body: {
                          to: recipient,
                          rows: filteredBudgetData,
                          totals: budgetTotals,
                          filters: { customer: budgetCustomerFilter, contact: budgetContactFilter, engineer: budgetEngineerFilter },
                        },
                      });
                      alert(result.message || 'Report sent!');
                    } catch (e) {
                      alert('Failed to send: ' + e.message);
                    }
                  }}
                >
                  Email Report
                </button>
              </div>

              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Customer</th>
                      <th>Contact</th>
                      <th>PO #</th>
                      <th>PO Amount</th>
                      <th>Hours Used</th>
                      <th>Hours Rem.</th>
                      <th>Amount Billed</th>
                      <th>Remaining</th>
                      <th style={{ minWidth: 140 }}>Usage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBudgetData.map((row) => {
                      const pct = row.po_amount > 0 ? (row.amount_billed / row.po_amount) * 100 : 0;
                      const cls = pct >= 90 ? 'progress-danger' : pct >= 70 ? 'progress-warn' : 'progress-good';
                      return (
                        <tr key={row.id}>
                          <td><strong>{row.project_name}</strong></td>
                          <td>{row.customer_name}</td>
                          <td style={{ fontSize: 13 }}>{row.contact_name || '—'}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{row.po_number || '-'}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(row.po_amount)}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>{(row.total_hours || 0).toFixed(2)}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace', color: row.hours_remaining !== null && row.hours_remaining < 0 ? 'var(--danger)' : undefined }}>
                            {row.hours_remaining !== null ? row.hours_remaining.toFixed(2) : '—'}
                          </td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(row.amount_billed)}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace', color: row.remaining < 0 ? 'var(--danger)' : undefined }}>
                            {formatCurrency(row.remaining)}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div className="progress-bar" style={{ flex: 1 }}>
                                <div className={`progress-fill ${cls}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                              </div>
                              <span style={{ fontSize: 12, color: '#64748b', minWidth: 36 }}>{pct.toFixed(0)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                      <td colSpan={4}>Totals</td>
                      <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(budgetTotals.po)}</td>
                      <td></td>
                      <td></td>
                      <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(budgetTotals.billed)}</td>
                      <td style={{ fontFamily: 'DM Mono, monospace', color: budgetTotals.remaining < 0 ? 'var(--danger)' : undefined }}>
                        {formatCurrency(budgetTotals.remaining)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div className="stat-card">
                  <div className="stat-label">Total PO Value</div>
                  <div className="stat-value" style={{ fontSize: 22 }}>{formatCurrency(budgetTotals.po)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Billed</div>
                  <div className="stat-value" style={{ fontSize: 22 }}>{formatCurrency(budgetTotals.billed)}</div>
                </div>
                <div className="stat-card accent">
                  <div className="stat-label">Total Remaining</div>
                  <div className="stat-value" style={{ fontSize: 22 }}>{formatCurrency(budgetTotals.remaining)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Overall Usage</div>
                  <div className="stat-value">
                    {budgetTotals.po > 0 ? ((budgetTotals.billed / budgetTotals.po) * 100).toFixed(1) : 0}%
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'contract-hours' && (
        <div className="card">
          <div className="card-title">Contract Hours Remaining</div>
          <p style={{ color: '#64748b', marginBottom: 16, fontSize: 14 }}>
            Shows remaining budget for active hourly projects. Hours are shown for single-engineer contracts; dollars for multi-engineer contracts.
          </p>

          {loading ? (
            <div style={{ padding: 40, color: '#94a3b8', textAlign: 'center' }}>Loading...</div>
          ) : contractHoursData.length === 0 ? (
            <div className="empty-state">
              <h3>No active hourly projects</h3>
              <p>Create hourly projects with engineers assigned to see remaining hours.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Customer</th>
                    <th>PO #</th>
                    <th>Engineers</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {contractHoursData.map((row) => {
                    const isOverBudget = row.remaining_dollars < 0;
                    const showHours = row.engineer_count === 1 && row.remaining_hours !== null;
                    return (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.project_name}</strong>
                          {row.single_engineer && (
                            <div style={{ fontSize: 12, color: '#64748b' }}>{row.single_engineer}</div>
                          )}
                        </td>
                        <td>{row.customer_name}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{row.po_number || '-'}</td>
                        <td style={{ textAlign: 'center' }}>{row.engineer_count}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, color: isOverBudget ? 'var(--danger)' : 'var(--success)' }}>
                          {showHours ? (
                            <>
                              {row.remaining_hours >= 0 ? row.remaining_hours.toFixed(1) : `(${Math.abs(row.remaining_hours).toFixed(1)})`} hrs
                              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{formatCurrency(row.remaining_dollars)}</div>
                            </>
                          ) : (
                            formatCurrency(row.remaining_dollars)
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {achModal && (() => {
        const selectedCount = Object.values(achSelections).filter(v => v.selected).length;
        const selectedTotal = Object.values(achSelections)
          .filter(v => v.selected)
          .reduce((sum, v) => sum + (parseFloat(v.amount) || 0), 0);
        return (
        <Modal
          title="Generate ACH File"
          onClose={() => setAchModal(false)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setAchModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAchExport} disabled={selectedCount === 0}>
                Download CSV ({selectedCount} engineer{selectedCount !== 1 ? 's' : ''} — {formatCurrency(selectedTotal)})
              </button>
            </>
          }
        >
          <p style={{ marginBottom: 16 }}>
            Generate a Chase-compatible ACH CSV file for the payroll period <strong>{formatDate(dateRange.period_start)}</strong> to <strong>{formatDate(dateRange.period_end)}</strong>.
          </p>

          <div className="form-group">
            <label className="form-label">Delivery Date</label>
            <input
              className="form-input"
              type="date"
              value={achDeliveryDate}
              onChange={(e) => setAchDeliveryDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Engineer Selection Table */}
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label className="form-label" style={{ margin: 0 }}>Select Engineers & Amounts</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const updated = { ...achSelections };
                    Object.keys(updated).forEach(k => { updated[k] = { ...updated[k], selected: true }; });
                    setAchSelections(updated);
                  }}
                >Select All</button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const updated = { ...achSelections };
                    Object.keys(updated).forEach(k => { updated[k] = { ...updated[k], selected: false }; });
                    setAchSelections(updated);
                  }}
                >Select None</button>
              </div>
            </div>
            <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>Engineer</th>
                    <th style={{ width: 100 }}>Gross</th>
                    {hasAnyAdvances && <th style={{ width: 100 }}>Advances</th>}
                    <th style={{ width: 130 }}>Net Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(achSelections).map(([name, val]) => (
                    <tr key={name} style={{ opacity: val.selected ? 1 : val.fullyPaidByAdvance ? 0.7 : 0.5, background: val.fullyPaidByAdvance ? '#fefce8' : undefined }}>
                      <td>
                        <input
                          type="checkbox"
                          checked={val.selected}
                          onChange={(e) => setAchSelections({
                            ...achSelections,
                            [name]: { ...val, selected: e.target.checked }
                          })}
                          disabled={val.fullyPaidByAdvance}
                        />
                      </td>
                      <td>
                        <strong>{name}</strong>
                        {val.fullyPaidByAdvance && (
                          <div style={{ fontSize: 10, color: '#92400e', marginTop: 2 }}>Paid via advance — will be cleared</div>
                        )}
                      </td>
                      <td style={{ fontFamily: 'DM Mono, monospace', color: '#64748b', fontSize: 12 }}>
                        {formatCurrency(val.grossPay || val.defaultAmount)}
                      </td>
                      {hasAnyAdvances && (
                        <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, color: val.advanceDeduction > 0 ? '#dc2626' : '#94a3b8' }}>
                          {val.advanceDeduction > 0 ? `(${formatCurrency(val.advanceDeduction)})` : '—'}
                        </td>
                      )}
                      <td>
                        {val.fullyPaidByAdvance ? (
                          <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, color: '#94a3b8' }}>$0.00</span>
                        ) : (
                          <input
                            className="form-input"
                            type="number"
                            step="0.01"
                            min="0"
                            value={val.amount}
                            onChange={(e) => setAchSelections({
                              ...achSelections,
                              [name]: { ...val, amount: parseFloat(e.target.value) || 0 }
                            })}
                            disabled={!val.selected}
                            style={{ width: '100%', padding: '4px 8px', fontSize: 13, fontFamily: 'DM Mono, monospace' }}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                    <td></td>
                    <td>Total</td>
                    <td></td>
                    <td style={{ fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(selectedTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="alert alert-info" style={{ marginTop: 16 }}>
            <strong>Before uploading to Chase:</strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              <li>Ensure all engineers have banking info entered</li>
              <li>Verify the payroll amounts are correct</li>
              <li>Upload via Chase Business Online → Payments → File Upload</li>
            </ul>
          </div>
        </Modal>
        );
      })()}

      {activeTab === 'engineer-payments' && (
        <div>
          {/* Sub-tabs for Engineer Payments */}
          <div className="card no-print" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              <button className={`btn btn-sm ${!showReconciliation && !showVerification && summary1099.length === 0 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setVerificationData(null); setSummary1099([]); setShowReconciliation(false); setShowVerification(false); }}>Payment History</button>
              <button className={`btn btn-sm ${summary1099.length > 0 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { load1099Summary(); setVerificationData(null); setShowReconciliation(false); setShowVerification(false); }}>1099 Summary</button>
              <button className={`btn btn-sm ${showVerification || verificationData ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setSummary1099([]); setShowReconciliation(false); setShowVerification(true); }}>Verification Letter</button>
              <button className={`btn btn-sm ${showReconciliation ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setShowReconciliation(true); setSummary1099([]); setVerificationData(null); setShowVerification(false); }}>Reconciliation</button>
            </div>
          </div>

          {/* Overpayment Warning Banner */}
          {overpayments.length > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderLeft: '4px solid #dc2626', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 16 }}>&#9888;</span>
                <strong style={{ color: '#dc2626', fontSize: 14 }}>Overpayment Alert</strong>
              </div>
              {overpayments.map(op => (
                <div key={op.engineer_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 13 }}>
                  <span><strong>{op.engineer_name}</strong> — owed ${op.total_owed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, paid ${op.total_paid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: '#dc2626' }}>Overpaid ${op.total_overpaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <button style={{ fontSize: 11, padding: '2px 8px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer' }} onClick={() => { setWriteoffTarget(op); setWriteoffReason(''); }}>Write Off</button>
                  </span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>Run a Reconciliation Report for full details.</div>
            </div>
          )}

          {writeoffTarget && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>Write Off Overpayment</div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                  This will mark <strong>${writeoffTarget.total_overpaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> for <strong>{writeoffTarget.engineer_name}</strong> as a write-off. The overpayment alert will be removed.
                </div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Reason *</label>
                <textarea value={writeoffReason} onChange={(e) => setWriteoffReason(e.target.value)} placeholder="e.g., Engineer deceased - not recoverable" rows={3} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button onClick={() => setWriteoffTarget(null)} style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  <button onClick={handleWriteoff} disabled={!writeoffReason.trim()} style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: writeoffReason.trim() ? '#dc2626' : '#e5e7eb', color: '#fff', cursor: writeoffReason.trim() ? 'pointer' : 'default', fontSize: 13, fontWeight: 500 }}>Confirm Write-Off</button>
                </div>
              </div>
            </div>
          )}

          {/* New Adjustment Form */}
          {summary1099.length === 0 && !verificationData && !showReconciliation && !showVerification && (
            <div style={{ marginBottom: 16 }}>
              {!showAdjForm && (
                <button onClick={() => setShowAdjForm(true)} style={{ fontSize: 13, padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#64748b', marginBottom: 12 }}>+ Create Payment Adjustment</button>
              )}
              {showAdjForm && (
                <div className="card no-print" style={{ marginBottom: 16, borderLeft: '4px solid #f59e0b' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: '#b45309' }}>Create Payment Adjustment</div>
                  <form onSubmit={handleCreateAdjustment}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div>
                        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Engineer *</label>
                        <select className="form-select" value={adjForm.user_id} onChange={(e) => setAdjForm({ ...adjForm, user_id: e.target.value })} style={{ width: 200 }}>
                          <option value="">Select...</option>
                          {engineers.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Amount * <span style={{ fontSize: 11, color: '#a8a29e' }}>(negative to deduct)</span></label>
                        <input className="form-input" type="number" step="0.01" value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} placeholder="-500.00" style={{ width: 140 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Reason *</label>
                        <input className="form-input" value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} placeholder="e.g., Customer rejected invoice - PO fully paid, deduct from next payment" style={{ width: '100%' }} />
                      </div>
                      <button className="btn btn-primary" type="submit" disabled={adjSaving} style={{ background: '#f59e0b', borderColor: '#f59e0b' }}>
                        {adjSaving ? 'Saving...' : 'Create Adjustment'}
                      </button>
                      <button type="button" onClick={() => { setShowAdjForm(false); setAdjForm({ user_id: '', amount: '', reason: '' }); }} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}

          {/* Record Payment Form */}
          {summary1099.length === 0 && !verificationData && !showReconciliation && !showVerification && (
            <div className="card no-print" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Record Payment</div>
              <form onSubmit={handleAddEngPayment}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Engineer *</label>
                    <select className="form-select" value={engPayForm.user_id} onChange={(e) => setEngPayForm({ ...engPayForm, user_id: e.target.value })} style={{ width: 200 }}>
                      <option value="">Select...</option>
                      {engineers.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Amount *</label>
                    <input className="form-input" type="number" step="0.01" value={engPayForm.amount} onChange={(e) => setEngPayForm({ ...engPayForm, amount: e.target.value })} placeholder="0.00" style={{ width: 120 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Date *</label>
                    <input className="form-input" type="date" value={engPayForm.payment_date} onChange={(e) => setEngPayForm({ ...engPayForm, payment_date: e.target.value })} style={{ width: 150 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Type</label>
                    <select className="form-select" value={engPayForm.payment_type} onChange={(e) => setEngPayForm({ ...engPayForm, payment_type: e.target.value })} style={{ width: 140 }}>
                      <option value="advance">Advance</option>
                      <option value="bonus">Bonus</option>
                      <option value="reimbursement">Reimbursement</option>
                      <option value="payroll">Payroll</option>
                      <option value="adjustment">Adjustment</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Method</label>
                    <select className="form-select" value={engPayForm.payment_method} onChange={(e) => setEngPayForm({ ...engPayForm, payment_method: e.target.value })} style={{ width: 130 }}>
                      <option value="">Select...</option>
                      <option value="ACH">ACH</option>
                      <option value="Check">Check</option>
                      <option value="Cash">Cash</option>
                      <option value="Zelle">Zelle</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Reference #</label>
                    <input className="form-input" value={engPayForm.reference_number} onChange={(e) => setEngPayForm({ ...engPayForm, reference_number: e.target.value })} placeholder="Check #, etc." style={{ width: 140 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Work Period</label>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <input className="form-input" type="date" value={engPayForm.period_start} onChange={(e) => setEngPayForm({ ...engPayForm, period_start: e.target.value })} style={{ width: 130 }} />
                      <span>-</span>
                      <input className="form-input" type="date" value={engPayForm.period_end} onChange={(e) => setEngPayForm({ ...engPayForm, period_end: e.target.value })} style={{ width: 130 }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Notes</label>
                    <input className="form-input" value={engPayForm.notes} onChange={(e) => setEngPayForm({ ...engPayForm, notes: e.target.value })} placeholder="Optional" style={{ width: 180 }} />
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={engPaySaving}>
                    {engPaySaving ? 'Saving...' : 'Record Payment'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Payment History Filter & Table */}
          {summary1099.length === 0 && !verificationData && !showReconciliation && !showVerification && (
            <div className="card">
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Payment History</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Engineer</label>
                  <select className="form-select" value={engPayFilter.user_id} onChange={(e) => { const f = { ...engPayFilter, user_id: e.target.value }; setEngPayFilter(f); loadEngPayments(f); }} style={{ width: 200 }}>
                    <option value="">All Engineers</option>
                    {engineers.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>From</label>
                  <input className="form-input" type="date" value={engPayFilter.period_start} onChange={(e) => { const f = { ...engPayFilter, period_start: e.target.value }; setEngPayFilter(f); loadEngPayments(f); }} style={{ width: 150 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>To</label>
                  <input className="form-input" type="date" value={engPayFilter.period_end} onChange={(e) => { const f = { ...engPayFilter, period_end: e.target.value }; setEngPayFilter(f); loadEngPayments(f); }} style={{ width: 150 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Type</label>
                  <select className="form-select" value={engPayFilter.payment_type} onChange={(e) => { const f = { ...engPayFilter, payment_type: e.target.value }; setEngPayFilter(f); loadEngPayments(f); }} style={{ width: 140 }}>
                    <option value="">All Types</option>
                    <option value="payroll">Payroll</option>
                    <option value="advance">Advance</option>
                    <option value="bonus">Bonus</option>
                    <option value="reimbursement">Reimbursement</option>
                    <option value="adjustment">Adjustment</option>
                  </select>
                </div>
              </div>

              {engPayments.length === 0 && !loading && (
                <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>No payments found.</div>
              )}

              {engPayments.length > 0 && (
                <div className="table-wrap">
                  <table style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Engineer</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Reference</th>
                        <th>Period</th>
                        <th>Notes</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {engPayments.map(p => {
                        const isEditing = editingPayment && editingPayment.id === p.id;
                        return isEditing ? (
                          <tr key={p.id} style={{ background: '#eff6ff' }}>
                            <td><input className="form-input" type="date" value={editingPayment.payment_date} onChange={(e) => setEditingPayment({ ...editingPayment, payment_date: e.target.value })} style={{ width: 130, fontSize: 11 }} /></td>
                            <td>{p.engineer_name}</td>
                            <td>
                              <select className="form-select" value={editingPayment.payment_type} onChange={(e) => setEditingPayment({ ...editingPayment, payment_type: e.target.value })} style={{ width: 110, fontSize: 11 }}>
                                <option value="payroll">Payroll</option>
                                <option value="advance">Advance</option>
                                <option value="bonus">Bonus</option>
                                <option value="reimbursement">Reimbursement</option>
                                <option value="adjustment">Adjustment</option>
                              </select>
                            </td>
                            <td><input className="form-input" type="number" step="0.01" value={editingPayment.amount} onChange={(e) => setEditingPayment({ ...editingPayment, amount: e.target.value })} style={{ width: 100, fontSize: 11, fontFamily: 'DM Mono, monospace' }} /></td>
                            <td>
                              <select className="form-select" value={editingPayment.payment_method || ''} onChange={(e) => setEditingPayment({ ...editingPayment, payment_method: e.target.value })} style={{ width: 80, fontSize: 11 }}>
                                <option value="">—</option>
                                <option value="ACH">ACH</option>
                                <option value="Check">Check</option>
                                <option value="Cash">Cash</option>
                                <option value="Zelle">Zelle</option>
                                <option value="Other">Other</option>
                              </select>
                            </td>
                            <td><input className="form-input" value={editingPayment.reference_number || ''} onChange={(e) => setEditingPayment({ ...editingPayment, reference_number: e.target.value })} style={{ width: 100, fontSize: 11 }} /></td>
                            <td style={{ fontSize: 11 }}><input className="form-input" type="date" value={editingPayment.period_start || ''} onChange={(e) => setEditingPayment({ ...editingPayment, period_start: e.target.value })} style={{ width: 120, fontSize: 11 }} /> - <input className="form-input" type="date" value={editingPayment.period_end || ''} onChange={(e) => setEditingPayment({ ...editingPayment, period_end: e.target.value })} style={{ width: 120, fontSize: 11 }} /></td>
                            <td><input className="form-input" value={editingPayment.notes || ''} onChange={(e) => setEditingPayment({ ...editingPayment, notes: e.target.value })} style={{ width: 140, fontSize: 11 }} /></td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-primary btn-sm" onClick={handleSaveEditPayment} disabled={engPaySaving} style={{ marginRight: 4 }}>Save</button>
                              <button className="btn btn-secondary btn-sm" onClick={() => setEditingPayment(null)}>Cancel</button>
                            </td>
                          </tr>
                        ) : (
                          <tr key={p.id}>
                            <td>{formatDate(p.payment_date)}</td>
                            <td>{p.engineer_name}</td>
                            <td>
                              <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', background: p.payment_type === 'payroll' ? '#dbeafe' : p.payment_type === 'advance' ? '#fef3c7' : '#f3e8ff', color: p.payment_type === 'payroll' ? '#1e40af' : p.payment_type === 'advance' ? '#92400e' : '#6b21a8' }}>{p.payment_type}</span>
                              {p.payment_type === 'advance' && (
                                <span
                                  style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: 'pointer', background: p.cleared ? '#dcfce7' : '#fef2f2', color: p.cleared ? '#166534' : '#991b1b' }}
                                  title={p.cleared ? `Cleared: ${p.cleared_payroll_period || ''}. Click to unmark.` : 'Uncleared — will be deducted from next payroll. Click to mark cleared.'}
                                  onClick={async () => {
                                    try {
                                      await apiFetch(`/engineer-payments/${p.id}/clear`, {
                                        method: 'PUT',
                                        body: { cleared: !p.cleared, cleared_payroll_period: !p.cleared ? 'Manual' : null }
                                      });
                                      loadEngPayments();
                                    } catch (e) { alert('Error: ' + e.message); }
                                  }}
                                >
                                  {p.cleared ? `Cleared${p.cleared_payroll_period ? ': ' + p.cleared_payroll_period : ''}` : 'Uncleared'}
                                </span>
                              )}
                            </td>
                            <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{formatCurrency(p.amount)}</td>
                            <td>{p.payment_method || '—'}</td>
                            <td style={{ fontSize: 11 }}>{p.reference_number || '—'}</td>
                            <td style={{ fontSize: 11 }}>{p.period_start && p.period_end ? `${formatDate(p.period_start)} - ${formatDate(p.period_end)}` : '—'}</td>
                            <td style={{ fontSize: 11, color: '#64748b' }}>{p.notes || ''}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-secondary btn-sm" onClick={() => setEditingPayment({ ...p })} style={{ marginRight: 4 }}>Edit</button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleDeleteEngPayment(p.id)}>Del</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f8fafc', fontWeight: 600 }}>
                        <td colSpan="3" style={{ textAlign: 'right' }}>Total:</td>
                        <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(engPayments.reduce((s, p) => s + p.amount, 0))}</td>
                        <td colSpan="5"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* 1099 Summary */}
          {summary1099.length > 0 && !verificationData && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>1099-NEC Summary — Tax Year {taxYear}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select className="form-select no-print" value={taxYear} onChange={(e) => setTaxYear(e.target.value)} style={{ width: 100 }}>
                    {yearOptions.map(y => <option key={y.label} value={y.label}>{y.label}</option>)}
                  </select>
                  <button className="btn btn-secondary btn-sm no-print" onClick={load1099Summary}>Refresh</button>
                  <button className="btn btn-secondary btn-sm no-print" onClick={() => window.print()}>Print</button>
                </div>
              </div>
              <div className="table-wrap">
                <table style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Engineer</th>
                      <th>Engineer ID</th>
                      <th>Total Paid</th>
                      <th>Payment Count</th>
                      <th>First Payment</th>
                      <th>Last Payment</th>
                      <th>1099 Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary1099.map(row => (
                      <tr key={row.user_id}>
                        <td style={{ fontWeight: 600 }}>{row.engineer_name}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace' }}>{row.engineer_id || '—'}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{formatCurrency(row.total_paid)}</td>
                        <td>{row.payment_count}</td>
                        <td>{formatDate(row.first_payment)}</td>
                        <td>{formatDate(row.last_payment)}</td>
                        <td>{row.total_paid >= 600 ? <span style={{ color: '#dc2626', fontWeight: 600 }}>YES</span> : <span style={{ color: '#64748b' }}>No</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#f8fafc', fontWeight: 600 }}>
                      <td>Total</td>
                      <td></td>
                      <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(summary1099.reduce((s, r) => s + r.total_paid, 0))}</td>
                      <td>{summary1099.reduce((s, r) => s + r.payment_count, 0)}</td>
                      <td colSpan="3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
                A 1099-NEC is required for contractors paid $600 or more during the tax year.
              </div>
            </div>
          )}

          {/* Reconciliation Report */}
          {showReconciliation && (
            <div>
              <div className="card no-print" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Engineer Payment Reconciliation</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Engineer *</label>
                    <select className="form-select" value={reconciliationForm.user_id} onChange={(e) => setReconciliationForm({ ...reconciliationForm, user_id: e.target.value })} style={{ width: 200 }}>
                      <option value="">Select...</option>
                      {engineers.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>From</label>
                    <input className="form-input" type="date" value={reconciliationForm.period_start} onChange={(e) => setReconciliationForm({ ...reconciliationForm, period_start: e.target.value })} style={{ width: 150 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>To</label>
                    <input className="form-input" type="date" value={reconciliationForm.period_end} onChange={(e) => setReconciliationForm({ ...reconciliationForm, period_end: e.target.value })} style={{ width: 150 }} />
                  </div>
                  <button className="btn btn-primary" onClick={loadReconciliation} disabled={!reconciliationForm.user_id || loading}>
                    {loading ? 'Loading...' : 'Run Report'}
                  </button>
                  {reconciliationData && (
                    <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
                  )}
                </div>
              </div>

              {reconciliationData && (
                <div className="card">
                  {/* Print Header */}
                  <div className="print-only" style={{ marginBottom: 20, textAlign: 'center' }}>
                    <h1 style={{ margin: 0, fontSize: 24 }}>Payment Reconciliation</h1>
                    <p style={{ margin: '8px 0 0', color: '#666' }}>
                      {reconciliationData.engineer.name} — {formatDate(reconciliationData.period_start)} to {formatDate(reconciliationData.period_end)}
                    </p>
                  </div>

                  <div className="no-print" style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>
                    {reconciliationData.engineer.name}
                    {reconciliationData.engineer.engineer_id && <span style={{ color: '#64748b', fontWeight: 400 }}> ({reconciliationData.engineer.engineer_id})</span>}
                  </div>

                  {/* Work / Hours Owed Section */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#475569' }}>Hours Worked &amp; Amounts Owed</div>
                    <div className="table-wrap">
                      <table style={{ fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th>Project</th>
                            <th>Type</th>
                            <th style={{ textAlign: 'right' }}>Submitted Hrs</th>
                            <th style={{ textAlign: 'right' }}>Approved Hrs</th>
                            <th style={{ textAlign: 'right' }}>Rate</th>
                            <th style={{ textAlign: 'right' }}>Fixed Amount</th>
                            <th style={{ textAlign: 'right' }}>Total Owed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reconciliationData.work.map((w, idx) => (
                            <tr key={idx}>
                              <td>{w.project_name}</td>
                              <td style={{ textTransform: 'capitalize', fontSize: 11 }}>{w.project_type.replace('_', ' ')}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{w.submitted_hours > 0 ? w.submitted_hours.toFixed(2) : '—'}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{w.approved_hours > 0 ? w.approved_hours.toFixed(2) : '—'}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#64748b' }}>
                                {w.project_type === 'hourly' ? `${formatCurrency(w.pay_rate)}/hr` : w.project_type === 'piece_rate' ? `${formatCurrency(w.pay_rate)}/unit` : w.project_type === 'fixed_monthly' ? `${formatCurrency(w.monthly_pay)}/mo` : 'Fixed'}
                              </td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#7c3aed' }}>
                                {w.fixed_amount > 0 ? formatCurrency(w.fixed_amount) : '—'}
                              </td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{formatCurrency(w.submitted_amount_owed)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                            <td colSpan={2}>Totals</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{reconciliationData.work.reduce((s, w) => s + w.submitted_hours, 0).toFixed(2)}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{reconciliationData.work.reduce((s, w) => s + w.approved_hours, 0).toFixed(2)}</td>
                            <td></td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#7c3aed' }}>{formatCurrency(reconciliationData.work.reduce((s, w) => s + (w.fixed_amount || 0), 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(reconciliationData.total_submitted_owed)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Payments Made Section */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#475569' }}>Payments Made</div>
                    {reconciliationData.payments.length === 0 ? (
                      <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 12 }}>No payments recorded in this period.</div>
                    ) : (
                      <div className="table-wrap">
                        <table style={{ fontSize: 12 }}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Type</th>
                              <th>Method</th>
                              <th>Reference</th>
                              <th style={{ textAlign: 'right' }}>Amount</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reconciliationData.payments.map(p => (
                              <tr key={p.id}>
                                <td>{formatDate(p.payment_date)}</td>
                                <td style={{ textTransform: 'capitalize', fontSize: 11 }}>{p.payment_type}</td>
                                <td>{p.payment_method || '—'}</td>
                                <td style={{ fontSize: 11 }}>{p.reference_number || '—'}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600, color: '#16a34a' }}>{formatCurrency(p.amount)}</td>
                                <td style={{ fontSize: 11, color: '#64748b' }}>{p.notes || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                              <td colSpan={4}>Total Paid</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(reconciliationData.total_paid)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                    <div className="stat-card">
                      <div className="stat-label">Total Owed</div>
                      <div className="stat-value" style={{ fontSize: 22 }}>{formatCurrency(reconciliationData.total_submitted_owed)}</div>
                    </div>
                    <div className="stat-card" style={{ borderLeft: '4px solid #7c3aed' }}>
                      <div className="stat-label">Fixed Amounts</div>
                      <div className="stat-value" style={{ fontSize: 22, color: '#7c3aed' }}>{formatCurrency(reconciliationData.work.reduce((s, w) => s + (w.fixed_amount || 0), 0))}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Total Paid</div>
                      <div className="stat-value" style={{ fontSize: 22, color: 'var(--success)' }}>{formatCurrency(reconciliationData.active_paid)}</div>
                      {reconciliationData.historical_paid > 0 && (
                        <div className="stat-sub" style={{ fontSize: 11, color: '#94a3b8' }}>+ {formatCurrency(reconciliationData.historical_paid)} historical</div>
                      )}
                    </div>
                    <div className="stat-card" style={{ borderLeft: `4px solid ${reconciliationData.balance > 0 ? '#dc2626' : reconciliationData.balance < 0 ? '#f59e0b' : '#16a34a'}` }}>
                      <div className="stat-label">{reconciliationData.balance > 0 ? 'Still Owed' : reconciliationData.balance < 0 ? 'Overpaid' : 'Balanced'}</div>
                      <div className="stat-value" style={{ fontSize: 22, color: reconciliationData.balance > 0 ? '#dc2626' : reconciliationData.balance < 0 ? '#f59e0b' : '#16a34a' }}>
                        {formatCurrency(Math.abs(reconciliationData.balance))}
                      </div>
                      {reconciliationData.historical_paid > 0 && (
                        <div className="stat-sub" style={{ fontSize: 11, color: '#94a3b8' }}>excludes historical imports</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Verification Letter */}
          {showVerification && (
            <div>
              {!verificationData && (
                <div className="card no-print" style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Employment / Payment Verification Letter</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                      <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Engineer</label>
                      <select className="form-select" value={verificationEngineer} onChange={(e) => setVerificationEngineer(e.target.value)} style={{ width: 200 }}>
                        <option value="">Select...</option>
                        {engineers.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>From</label>
                      <input className="form-input" type="date" value={verificationRange.period_start} onChange={(e) => setVerificationRange({ ...verificationRange, period_start: e.target.value })} style={{ width: 150 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>To</label>
                      <input className="form-input" type="date" value={verificationRange.period_end} onChange={(e) => setVerificationRange({ ...verificationRange, period_end: e.target.value })} style={{ width: 150 }} />
                    </div>
                    <button className="btn btn-primary" onClick={loadVerification}>Generate Letter</button>
                  </div>
                </div>
              )}

              {verificationData && (
                <div>
                  <div className="no-print" style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" onClick={() => setVerificationData(null)}>Back</button>
                    <button className="btn btn-primary" onClick={() => window.print()}>Print Letter</button>
                  </div>
                  {/* Zero page margin keeps the browser from printing its title, URL and date around the letter */}
                  <style>{`
                    @media print {
                      @page { size: letter; margin: 0; }
                      .verification-letter.card { max-width: none !important; padding: 0.6in 0.75in !important; }
                      .verification-letter .letter-logo { top: 0.6in !important; right: 0.75in !important; }
                      .verification-letter th, .verification-letter td { padding: 3px 8px !important; }
                      .verification-letter .letter-summary td { border: none !important; }
                      .verification-letter .letter-signature { break-inside: avoid; }
                    }
                  `}</style>
                  <div className="card verification-letter" style={{ position: 'relative', maxWidth: 800, margin: '0 auto', padding: 40, fontFamily: 'Georgia, serif', fontSize: 13, lineHeight: 1.6 }}>
                    {verificationData.company.logo && (
                      <img
                        className="letter-logo"
                        src={verificationData.company.logo}
                        alt="Company logo"
                        style={{ position: 'absolute', top: 40, right: 40, maxWidth: 160, maxHeight: 64, objectFit: 'contain' }}
                      />
                    )}
                    {/* Company Header */}
                    <div style={{ textAlign: 'center', marginBottom: 20 }}>
                      <div style={{ fontSize: 18, fontWeight: 'bold' }}>{verificationData.company.name}</div>
                      {verificationData.company.address && <div style={{ fontSize: 12 }}>{verificationData.company.address}</div>}
                      <div style={{ fontSize: 12 }}>
                        {verificationData.company.phone && <span>{verificationData.company.phone}</span>}
                        {verificationData.company.phone && verificationData.company.email && <span> | </span>}
                        {verificationData.company.email && <span>{verificationData.company.email}</span>}
                      </div>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                      {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>

                    <div style={{ marginBottom: 16, fontWeight: 'bold' }}>
                      RE: Employment and Payment Verification
                    </div>

                    <div style={{ marginBottom: 12 }}>To Whom It May Concern,</div>

                    <div style={{ marginBottom: 12 }}>
                      This letter is to confirm that <strong>{verificationData.engineer.name}</strong>
                      {verificationData.engineer.engineer_id && <span> (ID: {verificationData.engineer.engineer_id})</span>}
                      {' '}has been engaged as an independent contractor with {verificationData.company.name || 'our company'} since{' '}
                      {verificationData.engineer.engaged_since
                        ? new Date(verificationData.engineer.engaged_since.substring(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                        : 'the date of first engagement'}.
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      For the period of{' '}
                      <strong>{new Date(verificationData.period.start + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                      {' '}through{' '}
                      <strong>{new Date(verificationData.period.end + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>,
                      the following compensation was provided:
                    </div>

                    {/* Payment Details */}
                    {verificationData.payments && verificationData.payments.length > 0 && (
                      <div style={{ margin: '12px 20px' }}>
                        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #333' }}>
                              <th style={{ padding: '4px 8px', textAlign: 'left' }}>Period</th>
                              <th style={{ padding: '4px 8px', textAlign: 'center' }}>Date Paid</th>
                              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {verificationData.payments.map((p, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '3px 8px' }}>
                                  {p.period_start
                                    ? new Date(p.period_start + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                                    : new Date(p.payment_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </td>
                                <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                  {new Date(p.payment_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                                </td>
                                <td style={{ padding: '3px 8px', textAlign: 'right' }}>{formatCurrency(p.amount)}</td>
                              </tr>
                            ))}
                            <tr style={{ borderTop: '2px solid #333', fontWeight: 'bold' }}>
                              <td colSpan={2} style={{ padding: '4px 8px' }}>Total</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatCurrency(verificationData.total_paid)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Summary */}
                    <div style={{ margin: '12px 20px', padding: 10, border: '1px solid #ccc', background: '#fafafa' }}>
                      <table className="letter-summary" style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <tbody>
                          <tr><td style={{ padding: '3px 0' }}>Total Compensation:</td><td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(verificationData.total_paid)}</td></tr>
                          <tr><td style={{ padding: '3px 0' }}>Average Monthly Income:</td><td style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(verificationData.avg_monthly)}</td></tr>
                          <tr><td style={{ padding: '3px 0' }}>Months Active:</td><td style={{ textAlign: 'right' }}>{verificationData.months_active}</td></tr>
                        </tbody>
                      </table>
                    </div>

                    <div style={{ marginBottom: 12, marginTop: 12 }}>
                      This information is provided for verification purposes only. If you have any questions or require additional information, please do not hesitate to contact us.
                    </div>

                    <div className="letter-signature" style={{ marginTop: 30 }}>
                      <div>Sincerely,</div>
                      {verificationData.company.signature_image && (
                        <img
                          src={verificationData.company.signature_image}
                          alt="Signature"
                          style={{ maxWidth: 180, maxHeight: 50, marginTop: 10, display: 'block' }}
                        />
                      )}
                      <div style={{ marginTop: verificationData.company.signature_image ? 4 : 30, borderTop: '1px solid #000', width: 250, paddingTop: 4 }}>
                        {verificationData.company.authorized_signer_name || 'Authorized Representative'}
                      </div>
                      <div>{verificationData.company.name}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'profitability' && (() => {
        const filtered = profitData.filter(row => {
          if (profitCustomerFilter && row.customer_name !== profitCustomerFilter) return false;
          if (profitProjectFilter && row.project_id.toString() !== profitProjectFilter) return false;
          if (profitEngineerFilter && row.engineer_id.toString() !== profitEngineerFilter) return false;
          return true;
        });

        // Payments aren't recorded per project, so when filters hide some of an engineer's projects,
        // only the share of their pay matching the visible projects' earned cost is counted
        const sumCostByEngineer = (rows) => rows.reduce((acc, r) => ({ ...acc, [r.engineer_id]: (acc[r.engineer_id] || 0) + r.cost }), {});
        const allCostByEngineer = sumCostByEngineer(profitData);
        const visibleCostByEngineer = sumCostByEngineer(filtered);
        const paidInView = (engineerId) => {
          const paid = profitActualPaid[engineerId] || 0;
          const allCost = allCostByEngineer[engineerId] || 0;
          return allCost > 0 ? paid * ((visibleCostByEngineer[engineerId] || 0) / allCost) : paid;
        };

        const customers = [...new Set(profitData.map(r => r.customer_name))].sort();
        const projects = [...new Map(profitData.map(r => [r.project_id, { id: r.project_id, name: r.project_name, customer: r.customer_name }])).values()]
          .filter(p => !profitCustomerFilter || p.customer === profitCustomerFilter)
          .sort((a, b) => a.name.localeCompare(b.name));
        const engineerList = [...new Map(profitData.map(r => [r.engineer_id, { id: r.engineer_id, name: r.engineer_name }])).values()].sort((a, b) => a.name.localeCompare(b.name));

        const totals = filtered.reduce((acc, r) => ({
          hours: acc.hours + r.total_hours,
          billed: acc.billed + r.billed,
          cost: acc.cost + r.cost,
          profit: acc.profit + r.profit,
        }), { hours: 0, billed: 0, cost: 0, profit: 0 });
        const summaryTotalPaid = Object.values(filtered.reduce((acc, r) => { acc[r.engineer_id] = paidInView(r.engineer_id) || 0; return acc; }, {})).reduce((s, v) => s + v, 0);
        const summaryEffectiveCost = Math.max(totals.cost, summaryTotalPaid);
        const summaryProfit = totals.billed - summaryEffectiveCost;
        const totalMargin = totals.billed > 0 ? (summaryProfit / totals.billed) * 100 : 0;

        const engineerSummary = Object.values(filtered.reduce((acc, r) => {
          if (!acc[r.engineer_id]) {
            acc[r.engineer_id] = { engineer_id: r.engineer_id, engineer_name: r.engineer_name, engineer_code: r.engineer_code, hours: 0, billed: 0, cost: 0, profit: 0 };
          }
          acc[r.engineer_id].hours += r.total_hours;
          acc[r.engineer_id].billed += r.billed;
          acc[r.engineer_id].cost += r.cost;
          acc[r.engineer_id].profit += r.profit;
          return acc;
        }, {}));
        const sortedEngineerSummary = [...engineerSummary].sort((a, b) => {
          const k = profitSummarySort.key;
          let av, bv;
          if (k === 'actual_paid') { av = paidInView(a.engineer_id) || 0; bv = paidInView(b.engineer_id) || 0; }
          else if (k === 'over_under') { av = (paidInView(a.engineer_id) || 0) - a.cost; bv = (paidInView(b.engineer_id) || 0) - b.cost; }
          else if (k === 'actual_profit') { const pa = paidInView(a.engineer_id) || 0; av = a.billed - Math.max(a.cost, pa); const pb = paidInView(b.engineer_id) || 0; bv = b.billed - Math.max(b.cost, pb); }
          else if (k === 'margin') { const pa = paidInView(a.engineer_id) || 0; const apA = a.billed - Math.max(a.cost, pa); av = a.billed > 0 ? apA / a.billed : 0; const pb = paidInView(b.engineer_id) || 0; const apB = b.billed - Math.max(b.cost, pb); bv = b.billed > 0 ? apB / b.billed : 0; }
          else if (k === 'engineer_name') { av = a.engineer_name; bv = b.engineer_name; }
          else { av = a[k]; bv = b[k]; }
          if (typeof av === 'string') return profitSummarySort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
          return profitSummarySort.dir === 'asc' ? av - bv : bv - av;
        });

        const fmt = (v) => '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const mono = { fontFamily: 'DM Mono, monospace', textAlign: 'right' };
        const sortArrow = (sortState, key) => sortState.key === key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : '';
        const toggleSort = (setter, sortState, key) => setter({ key, dir: sortState.key === key && sortState.dir === 'asc' ? 'desc' : 'asc' });
        const thSort = { cursor: 'pointer', userSelect: 'none' };

        return (
          <div>
            <div className="card no-print" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Engineer Profitability</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Start Date</label>
                  <input type="date" className="form-input" value={profitRange.period_start} onChange={(e) => setProfitRange({ ...profitRange, period_start: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>End Date</label>
                  <input type="date" className="form-input" value={profitRange.period_end} onChange={(e) => setProfitRange({ ...profitRange, period_end: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Customer</label>
                  <select className="form-select" value={profitCustomerFilter} onChange={(e) => { setProfitCustomerFilter(e.target.value); setProfitProjectFilter(''); }}>
                    <option value="">All Customers</option>
                    {customers.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Project</label>
                  <select className="form-select" value={profitProjectFilter} onChange={(e) => setProfitProjectFilter(e.target.value)}>
                    <option value="">All Projects</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Engineer</label>
                  <select className="form-select" value={profitEngineerFilter} onChange={(e) => setProfitEngineerFilter(e.target.value)}>
                    <option value="">All Engineers</option>
                    {engineerList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <button className="btn btn-primary" onClick={() => loadProfitability()} disabled={loading}>
                  {loading ? 'Loading...' : 'Run Report'}
                </button>
              </div>
            </div>

            {profitData.length > 0 && (
              <>
                <div className="stat-grid" style={{ marginBottom: 16 }}>
                  <div className="stat-card">
                    <div className="stat-label">Total Billed</div>
                    <div className="stat-value" style={{ fontSize: 20 }}>{fmt(totals.billed)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Total Cost</div>
                    <div className="stat-value" style={{ fontSize: 20 }}>{fmt(totals.cost)}</div>
                  </div>
                  <div className="stat-card accent">
                    <div className="stat-label">Total Profit</div>
                    <div className="stat-value" style={{ fontSize: 20, color: summaryProfit >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(summaryProfit)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Margin</div>
                    <div className="stat-value" style={{ fontSize: 20, color: totalMargin >= 0 ? '#16a34a' : '#dc2626' }}>{totalMargin.toFixed(1)}%</div>
                  </div>
                </div>

                <div className="card" style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Profit by Engineer</div>
                  <div className="table-wrap">
                    <table style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={thSort} onClick={() => toggleSort(setProfitSummarySort, profitSummarySort, 'engineer_name')}>Engineer{sortArrow(profitSummarySort, 'engineer_name')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitSummarySort, profitSummarySort, 'hours')}>Hours{sortArrow(profitSummarySort, 'hours')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitSummarySort, profitSummarySort, 'billed')}>Billed{sortArrow(profitSummarySort, 'billed')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitSummarySort, profitSummarySort, 'cost')}>Cost (Earned){sortArrow(profitSummarySort, 'cost')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitSummarySort, profitSummarySort, 'actual_paid')}>Actual Paid{sortArrow(profitSummarySort, 'actual_paid')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitSummarySort, profitSummarySort, 'over_under')}>Over/Under{sortArrow(profitSummarySort, 'over_under')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitSummarySort, profitSummarySort, 'actual_profit')}>Profit{sortArrow(profitSummarySort, 'actual_profit')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitSummarySort, profitSummarySort, 'margin')}>Margin{sortArrow(profitSummarySort, 'margin')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedEngineerSummary.map(e => {
                          const paid = paidInView(e.engineer_id) || 0;
                          const effectiveCost = Math.max(e.cost, paid);
                          const actualProfit = e.billed - effectiveCost;
                          const m = e.billed > 0 ? (actualProfit / e.billed) * 100 : 0;
                          const overUnder = paid - e.cost;
                          return (
                            <tr key={e.engineer_id}>
                              <td><strong>{e.engineer_name}</strong>{e.engineer_code ? <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 11 }}>({e.engineer_code})</span> : ''}</td>
                              <td style={mono}>{e.hours.toFixed(1)}</td>
                              <td style={mono}>{fmt(e.billed)}</td>
                              <td style={mono}>{fmt(e.cost)}</td>
                              <td style={mono}>{fmt(paid)}</td>
                              <td style={{ ...mono, color: Math.abs(overUnder) < 0.01 ? undefined : overUnder > 0 ? '#dc2626' : '#16a34a', fontWeight: Math.abs(overUnder) >= 0.01 ? 600 : undefined }}>{Math.abs(overUnder) < 0.01 ? '-' : (overUnder > 0 ? '+' : '') + fmt(overUnder)}</td>
                              <td style={{ ...mono, color: actualProfit >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{fmt(actualProfit)}</td>
                              <td style={{ ...mono, color: m >= 0 ? '#16a34a' : '#dc2626' }}>{m.toFixed(1)}%</td>
                            </tr>
                          );
                        })}
                        {(() => {
                          const totalPaid = engineerSummary.reduce((s, e) => s + (paidInView(e.engineer_id) || 0), 0);
                          const totalOverUnder = totalPaid - totals.cost;
                          const totalEffectiveCost = Math.max(totals.cost, totalPaid);
                          const totalActualProfit = totals.billed - totalEffectiveCost;
                          const totalActualMargin = totals.billed > 0 ? (totalActualProfit / totals.billed) * 100 : 0;
                          return (
                            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                              <td>Total</td>
                              <td style={mono}>{totals.hours.toFixed(1)}</td>
                              <td style={mono}>{fmt(totals.billed)}</td>
                              <td style={mono}>{fmt(totals.cost)}</td>
                              <td style={mono}>{fmt(totalPaid)}</td>
                              <td style={{ ...mono, color: Math.abs(totalOverUnder) < 0.01 ? undefined : totalOverUnder > 0 ? '#dc2626' : '#16a34a' }}>{Math.abs(totalOverUnder) < 0.01 ? '-' : (totalOverUnder > 0 ? '+' : '') + fmt(totalOverUnder)}</td>
                              <td style={{ ...mono, color: totalActualProfit >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(totalActualProfit)}</td>
                              <td style={{ ...mono, color: totalActualMargin >= 0 ? '#16a34a' : '#dc2626' }}>{totalActualMargin.toFixed(1)}%</td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="card">
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Detail by Engineer / Project</div>
                  <div className="table-wrap">
                    <table style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={thSort} onClick={() => toggleSort(setProfitDetailSort, profitDetailSort, 'engineer_name')}>Engineer{sortArrow(profitDetailSort, 'engineer_name')}</th>
                          <th style={thSort} onClick={() => toggleSort(setProfitDetailSort, profitDetailSort, 'customer_name')}>Customer{sortArrow(profitDetailSort, 'customer_name')}</th>
                          <th style={thSort} onClick={() => toggleSort(setProfitDetailSort, profitDetailSort, 'project_name')}>Project{sortArrow(profitDetailSort, 'project_name')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitDetailSort, profitDetailSort, 'total_hours')}>Hours{sortArrow(profitDetailSort, 'total_hours')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitDetailSort, profitDetailSort, 'billed')}>Billed{sortArrow(profitDetailSort, 'billed')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitDetailSort, profitDetailSort, 'cost')}>Cost{sortArrow(profitDetailSort, 'cost')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitDetailSort, profitDetailSort, 'profit')}>Profit{sortArrow(profitDetailSort, 'profit')}</th>
                          <th style={{ ...thSort, textAlign: 'right' }} onClick={() => toggleSort(setProfitDetailSort, profitDetailSort, 'margin')}>Margin{sortArrow(profitDetailSort, 'margin')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...filtered].sort((a, b) => {
                          const k = profitDetailSort.key;
                          let av, bv;
                          const getDetailVals = (r) => {
                            const es = engineerSummary.find(e => e.engineer_id === r.engineer_id);
                            const p = paidInView(r.engineer_id) || 0;
                            const etc = es?.cost || 0;
                            const ec = Math.max(etc, p);
                            const cs = etc > 0 ? r.cost / etc : 0;
                            const dc = cs * ec;
                            const dp = r.billed - dc;
                            const dm = r.billed > 0 ? dp / r.billed : 0;
                            return { cost: dc, profit: dp, margin: dm };
                          };
                          if (k === 'cost' || k === 'profit' || k === 'margin') {
                            const va = getDetailVals(a); const vb = getDetailVals(b);
                            av = va[k]; bv = vb[k];
                          } else { av = a[k]; bv = b[k]; }
                          if (typeof av === 'string') return profitDetailSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                          return profitDetailSort.dir === 'asc' ? av - bv : bv - av;
                        }).map((r, i) => {
                          const engSummary = engineerSummary.find(e => e.engineer_id === r.engineer_id);
                          const paid = paidInView(r.engineer_id) || 0;
                          const engTotalCost = engSummary?.cost || 0;
                          const effectiveCost = Math.max(engTotalCost, paid);
                          const costShare = engTotalCost > 0 ? r.cost / engTotalCost : 0;
                          const detailCost = costShare * effectiveCost;
                          const detailProfit = r.billed - detailCost;
                          const detailMargin = r.billed > 0 ? (detailProfit / r.billed) * 100 : 0;
                          return (
                          <tr key={`${r.engineer_id}-${r.project_id}`}>
                            <td><strong>{r.engineer_name}</strong></td>
                            <td>{r.customer_name}</td>
                            <td>{r.project_name}</td>
                            <td style={mono}>{r.total_hours.toFixed(1)}</td>
                            <td style={mono}>{fmt(r.billed)}</td>
                            <td style={mono}>{fmt(detailCost)}</td>
                            <td style={{ ...mono, color: detailProfit >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{fmt(detailProfit)}</td>
                            <td style={{ ...mono, color: detailMargin >= 0 ? '#16a34a' : '#dc2626' }}>{detailMargin.toFixed(1)}%</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {profitData.length === 0 && !loading && (
              <div className="card" style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>
                Click "Run Report" to generate the profitability report.
              </div>
            )}
          </div>
        );
      })()}

      {activeTab === 'year-end' && (
        <div>
          <div className="card no-print" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 18 }}>Year-End Reports</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>Tax preparation and annual business summary</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label className="form-label" style={{ margin: 0, fontSize: 13 }}>Tax Year:</label>
                <select className="form-select" value={yearEndYear} onChange={(e) => setYearEndYear(e.target.value)} style={{ width: 100 }}>
                  {yearOptions.map(y => <option key={y.label} value={y.label}>{y.label}</option>)}
                </select>
                <button className="btn btn-primary" onClick={loadYearEndData} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate'}
                </button>
              </div>
            </div>
          </div>

          {!yearEndData && !loading && (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              Select a year and click Generate to build all year-end reports.
            </div>
          )}

          {loading && <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading year-end data...</div>}

          {yearEndData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

              {/* 1. 1099 Contractor Summary */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['1099'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('1099')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>1099 Contractor Summary</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {yearEndData.contractors1099.filter(c => c.total_paid >= 600).length} contractors require 1099
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['1099'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['1099'] && (
                  <div style={{ padding: 20 }}>
                    <div className="table-wrap">
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <SortHeader section="1099" field="engineer_name">Contractor</SortHeader>
                            <th>ID</th>
                            <SortHeader section="1099" field="total_paid" style={{ textAlign: 'right' }}>Total Paid</SortHeader>
                            <SortHeader section="1099" field="payment_count" style={{ textAlign: 'center' }}>Payments</SortHeader>
                            <SortHeader section="1099" field="first_payment">First Payment</SortHeader>
                            <SortHeader section="1099" field="last_payment">Last Payment</SortHeader>
                            <th style={{ textAlign: 'center' }}>1099 Required</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortYearEndData(yearEndData.contractors1099, '1099').map(c => (
                            <tr key={c.user_id}>
                              <td><strong>{c.engineer_name}</strong></td>
                              <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{c.engineer_id || '—'}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{formatCurrency(c.total_paid)}</td>
                              <td style={{ textAlign: 'center' }}>{c.payment_count}</td>
                              <td style={{ fontSize: 12 }}>{formatDate(c.first_payment)}</td>
                              <td style={{ fontSize: 12 }}>{formatDate(c.last_payment)}</td>
                              <td style={{ textAlign: 'center' }}>
                                {c.total_paid >= 600
                                  ? <span style={{ color: '#dc2626', fontWeight: 600 }}>YES</span>
                                  : <span style={{ color: '#94a3b8' }}>No</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                            <td colSpan={2}>Total</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.contractors1099.reduce((s, c) => s + c.total_paid, 0))}</td>
                            <td style={{ textAlign: 'center' }}>{yearEndData.contractors1099.reduce((s, c) => s + c.payment_count, 0)}</td>
                            <td colSpan={3}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                      1099-NEC required for contractors paid $600+ during the tax year. Due January 31.
                    </div>
                  </div>
                )}
              </div>

              {/* 2. Revenue by Customer */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['revenue'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('revenue')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>Revenue by Customer</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {formatCurrency(yearEndData.revenueByCustomer.reduce((s, c) => s + c.total_invoiced, 0))} total invoiced
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['revenue'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['revenue'] && (
                  <div style={{ padding: 20 }}>
                    <div className="table-wrap">
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <SortHeader section="revenue" field="customer_name">Customer</SortHeader>
                            <SortHeader section="revenue" field="invoice_count" style={{ textAlign: 'center' }}>Invoices</SortHeader>
                            <SortHeader section="revenue" field="total_invoiced" style={{ textAlign: 'right' }}>Total Invoiced</SortHeader>
                            <SortHeader section="revenue" field="total_collected" style={{ textAlign: 'right' }}>Collected</SortHeader>
                            <SortHeader section="revenue" field="outstanding" style={{ textAlign: 'right' }}>Outstanding</SortHeader>
                          </tr>
                        </thead>
                        <tbody>
                          {sortYearEndData(yearEndData.revenueByCustomer, 'revenue').map(c => (
                            <tr key={c.customer_id}>
                              <td><strong>{c.customer_name}</strong></td>
                              <td style={{ textAlign: 'center' }}>{c.invoice_count}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(c.total_invoiced)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(c.total_collected)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: c.outstanding > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(c.outstanding)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                            <td>Total</td>
                            <td style={{ textAlign: 'center' }}>{yearEndData.revenueByCustomer.reduce((s, c) => s + c.invoice_count, 0)}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.revenueByCustomer.reduce((s, c) => s + c.total_invoiced, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(yearEndData.revenueByCustomer.reduce((s, c) => s + c.total_collected, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.revenueByCustomer.reduce((s, c) => s + c.outstanding, 0))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Accounts Receivable */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['ar'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('ar')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>Accounts Receivable</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {formatCurrency(yearEndData.accountsReceivable.reduce((s, i) => s + i.balance, 0))} outstanding as of 12/31/{yearEndData.year}
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['ar'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['ar'] && (
                  <div style={{ padding: 20 }}>
                    {yearEndData.accountsReceivable.length === 0 ? (
                      <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>No outstanding receivables.</div>
                    ) : (
                      <div className="table-wrap">
                        <table style={{ fontSize: 13 }}>
                          <thead>
                            <tr>
                              <SortHeader section="ar" field="invoice_number">Invoice #</SortHeader>
                              <SortHeader section="ar" field="customer_name">Customer</SortHeader>
                              <SortHeader section="ar" field="project_name">Project</SortHeader>
                              <SortHeader section="ar" field="created_at">Invoice Date</SortHeader>
                              <SortHeader section="ar" field="due_date">Due Date</SortHeader>
                              <SortHeader section="ar" field="total_amount" style={{ textAlign: 'right' }}>Total</SortHeader>
                              <SortHeader section="ar" field="amount_paid" style={{ textAlign: 'right' }}>Paid</SortHeader>
                              <SortHeader section="ar" field="balance" style={{ textAlign: 'right' }}>Balance</SortHeader>
                              <SortHeader section="ar" field="days_overdue">Status</SortHeader>
                            </tr>
                          </thead>
                          <tbody>
                            {sortYearEndData(yearEndData.accountsReceivable, 'ar').map(inv => (
                              <tr key={inv.id}>
                                <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{inv.invoice_number}</td>
                                <td>{inv.customer_name}</td>
                                <td style={{ fontSize: 12 }}>{inv.project_name}</td>
                                <td style={{ fontSize: 12 }}>{formatDate(inv.created_at)}</td>
                                <td style={{ fontSize: 12 }}>{formatDate(inv.due_date)}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(inv.total_amount)}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(inv.amount_paid)}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{formatCurrency(inv.balance)}</td>
                                <td>
                                  <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: inv.days_overdue > 0 ? '#fecaca' : '#dcfce7', color: inv.days_overdue > 0 ? '#991b1b' : '#166534' }}>
                                    {inv.days_overdue > 0 ? `${inv.days_overdue}d overdue` : 'Current'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                              <td colSpan={5}>Total ({yearEndData.accountsReceivable.length} invoices)</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.accountsReceivable.reduce((s, i) => s + i.total_amount, 0))}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.accountsReceivable.reduce((s, i) => s + i.amount_paid, 0))}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.accountsReceivable.reduce((s, i) => s + i.balance, 0))}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 4. Income by Quarter */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['quarterly'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('quarterly')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>Income by Quarter</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {formatCurrency(yearEndData.incomeByQuarter.reduce((s, q) => s + q.total_collected, 0))} collected for the year
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['quarterly'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['quarterly'] && (
                  <div style={{ padding: 20 }}>
                    <div className="table-wrap">
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Quarter</th>
                            <th style={{ textAlign: 'center' }}>Invoices</th>
                            <th style={{ textAlign: 'right' }}>Invoiced</th>
                            <th style={{ textAlign: 'right' }}>Collected</th>
                            <th style={{ textAlign: 'right' }}>Contractor Pay</th>
                            <th style={{ textAlign: 'right' }}>Net Income</th>
                          </tr>
                        </thead>
                        <tbody>
                          {yearEndData.incomeByQuarter.map(q => (
                            <tr key={q.quarter}>
                              <td><strong>{q.quarter}</strong></td>
                              <td style={{ textAlign: 'center' }}>{q.invoice_count}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(q.total_invoiced)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(q.total_collected)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#dc2626' }}>{formatCurrency(q.total_paid)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600, color: q.net_income >= 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(q.net_income)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                            <td>Annual</td>
                            <td style={{ textAlign: 'center' }}>{yearEndData.incomeByQuarter.reduce((s, q) => s + q.invoice_count, 0)}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.incomeByQuarter.reduce((s, q) => s + q.total_invoiced, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(yearEndData.incomeByQuarter.reduce((s, q) => s + q.total_collected, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#dc2626' }}>{formatCurrency(yearEndData.incomeByQuarter.reduce((s, q) => s + q.total_paid, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{formatCurrency(yearEndData.incomeByQuarter.reduce((s, q) => s + q.net_income, 0))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                      Net Income = Collected Revenue - Contractor Payments. Useful for estimated quarterly tax payments.
                    </div>
                  </div>
                )}
              </div>

              {/* 5. Project Profitability */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['profit'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('profit')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>Project Profitability</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {yearEndData.projectProfitability.length} projects with activity
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['profit'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['profit'] && (
                  <div style={{ padding: 20 }}>
                    <div className="table-wrap">
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <SortHeader section="profit" field="project_name">Project</SortHeader>
                            <SortHeader section="profit" field="customer_name">Customer</SortHeader>
                            <SortHeader section="profit" field="project_type">Type</SortHeader>
                            <SortHeader section="profit" field="total_invoiced" style={{ textAlign: 'right' }}>Revenue</SortHeader>
                            <SortHeader section="profit" field="labor_cost" style={{ textAlign: 'right' }}>Labor Cost</SortHeader>
                            <SortHeader section="profit" field="gross_profit" style={{ textAlign: 'right' }}>Gross Profit</SortHeader>
                            <SortHeader section="profit" field="margin_pct" style={{ textAlign: 'right' }}>Margin</SortHeader>
                          </tr>
                        </thead>
                        <tbody>
                          {sortYearEndData(yearEndData.projectProfitability, 'profit').map(p => (
                            <tr key={p.id}>
                              <td><strong>{p.project_name}</strong></td>
                              <td>{p.customer_name}</td>
                              <td style={{ fontSize: 11 }}>
                                <span className={`badge ${p.project_type === 'fixed_price' || p.project_type === 'piece_rate' ? 'badge-fixed' : 'badge-hourly'}`}>{p.project_type === 'piece_rate' ? 'Piece Rate' : p.project_type === 'fixed_price' ? 'Fixed' : p.project_type === 'fixed_monthly' ? 'Monthly' : 'Hourly'}</span>
                              </td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(p.total_invoiced)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#7c3aed' }}>{formatCurrency(p.labor_cost)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600, color: p.gross_profit >= 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(p.gross_profit)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: p.margin_pct >= 0 ? '#16a34a' : '#dc2626' }}>{p.margin_pct.toFixed(1)}%</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                            <td colSpan={3}>Total</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.projectProfitability.reduce((s, p) => s + p.total_invoiced, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#7c3aed' }}>{formatCurrency(yearEndData.projectProfitability.reduce((s, p) => s + p.labor_cost, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(yearEndData.projectProfitability.reduce((s, p) => s + p.gross_profit, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>
                              {(() => { const rev = yearEndData.projectProfitability.reduce((s, p) => s + p.total_invoiced, 0); const prof = yearEndData.projectProfitability.reduce((s, p) => s + p.gross_profit, 0); return rev > 0 ? (prof / rev * 100).toFixed(1) + '%' : '—'; })()}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* 6. Engineer Utilization */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['utilization'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('utilization')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>Engineer Utilization</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {yearEndData.engineerUtilization.reduce((s, e) => s + e.total_hours, 0).toFixed(0)} total hours logged
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['utilization'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['utilization'] && (
                  <div style={{ padding: 20 }}>
                    <div className="table-wrap">
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <SortHeader section="utilization" field="engineer_name">Engineer</SortHeader>
                            <th>ID</th>
                            <SortHeader section="utilization" field="project_count" style={{ textAlign: 'center' }}>Projects</SortHeader>
                            <SortHeader section="utilization" field="total_hours" style={{ textAlign: 'right' }}>Hours Worked</SortHeader>
                            <SortHeader section="utilization" field="budgeted_hours" style={{ textAlign: 'right' }}>Budgeted Hours</SortHeader>
                            <SortHeader section="utilization" field="utilization_pct" style={{ textAlign: 'right' }}>Utilization</SortHeader>
                          </tr>
                        </thead>
                        <tbody>
                          {sortYearEndData(yearEndData.engineerUtilization, 'utilization').map(e => (
                            <tr key={e.user_id}>
                              <td><strong>{e.engineer_name}</strong></td>
                              <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{e.engineer_id || '—'}</td>
                              <td style={{ textAlign: 'center' }}>{e.project_count}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{e.total_hours.toFixed(2)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{e.budgeted_hours ? e.budgeted_hours.toFixed(0) : '—'}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>
                                {e.utilization_pct !== null ? (
                                  <span style={{ color: e.utilization_pct > 100 ? '#dc2626' : e.utilization_pct > 80 ? '#16a34a' : '#f59e0b' }}>
                                    {e.utilization_pct.toFixed(0)}%
                                  </span>
                                ) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                            <td colSpan={2}>Total</td>
                            <td></td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{yearEndData.engineerUtilization.reduce((s, e) => s + e.total_hours, 0).toFixed(2)}</td>
                            <td colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* 7. Aged Receivables */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['aged'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('aged')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>Aged Receivables</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {formatCurrency(Object.values(yearEndData.agedReceivables.buckets).reduce((s, v) => s + v, 0) - (yearEndData.agedReceivables.buckets.current || 0))} overdue
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['aged'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['aged'] && (
                  <div style={{ padding: 20 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 16 }}>
                      {[
                        { label: 'Current', key: 'current', color: '#10b981' },
                        { label: '1-30 Days', key: '1-30', color: '#f59e0b' },
                        { label: '31-60 Days', key: '31-60', color: '#f97316' },
                        { label: '61-90 Days', key: '61-90', color: '#ef4444' },
                        { label: '90+ Days', key: '90+', color: '#dc2626' },
                      ].map(bucket => (
                        <div key={bucket.key} style={{ padding: 12, textAlign: 'center', border: '1px solid var(--border)', borderRadius: 8 }}>
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{bucket.label}</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: bucket.color, fontFamily: 'DM Mono, monospace' }}>
                            {formatCurrency(yearEndData.agedReceivables.buckets[bucket.key] || 0)}
                          </div>
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>{yearEndData.agedReceivables.counts[bucket.key] || 0} inv.</div>
                        </div>
                      ))}
                    </div>
                    {yearEndData.agedReceivables.details.length > 0 && (
                      <div className="table-wrap">
                        <table style={{ fontSize: 12 }}>
                          <thead>
                            <tr>
                              <SortHeader section="aged" field="invoice_number">Invoice #</SortHeader>
                              <SortHeader section="aged" field="customer_name">Customer</SortHeader>
                              <SortHeader section="aged" field="due_date">Due Date</SortHeader>
                              <SortHeader section="aged" field="days_overdue" style={{ textAlign: 'center' }}>Days Overdue</SortHeader>
                              <SortHeader section="aged" field="balance" style={{ textAlign: 'right' }}>Balance</SortHeader>
                              <th>Aging</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortYearEndData(yearEndData.agedReceivables.details, 'aged', { field: 'days_overdue', dir: 'desc' }).map(inv => (
                              <tr key={inv.id}>
                                <td style={{ fontFamily: 'DM Mono, monospace' }}>{inv.invoice_number}</td>
                                <td>{inv.customer_name}</td>
                                <td>{formatDate(inv.due_date)}</td>
                                <td style={{ textAlign: 'center', fontWeight: 600 }}>{inv.days_overdue}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{formatCurrency(inv.balance)}</td>
                                <td>
                                  <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: inv.aging === '90+' ? '#fecaca' : '#fef3c7', color: inv.aging === '90+' ? '#991b1b' : '#92400e' }}>
                                    {inv.aging} days
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                      Invoices overdue 90+ days may be candidates for write-off. Consult your accountant.
                    </div>
                  </div>
                )}
              </div>

              {/* 8. Customer Concentration */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['concentration'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('concentration')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>Customer Concentration</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {yearEndData.customerConcentration.length} customers
                      {yearEndData.customerConcentration.length > 0 && yearEndData.customerConcentration[0].pct_of_revenue > 50 &&
                        <span style={{ color: '#f59e0b', marginLeft: 8 }}>Top customer is {yearEndData.customerConcentration[0].pct_of_revenue.toFixed(0)}% of revenue</span>
                      }
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['concentration'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['concentration'] && (
                  <div style={{ padding: 20 }}>
                    <div className="table-wrap">
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <SortHeader section="concentration" field="customer_name">Customer</SortHeader>
                            <SortHeader section="concentration" field="total_invoiced" style={{ textAlign: 'right' }}>Revenue</SortHeader>
                            <SortHeader section="concentration" field="pct_of_revenue" style={{ textAlign: 'right' }}>% of Total</SortHeader>
                            <th style={{ minWidth: 200 }}>Concentration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortYearEndData(yearEndData.customerConcentration, 'concentration').map(c => (
                            <tr key={c.customer_id}>
                              <td><strong>{c.customer_name}</strong></td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(c.total_invoiced)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{c.pct_of_revenue.toFixed(1)}%</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div className="progress-bar" style={{ flex: 1 }}>
                                    <div className={`progress-fill ${c.pct_of_revenue > 50 ? 'progress-danger' : c.pct_of_revenue > 30 ? 'progress-warn' : 'progress-good'}`} style={{ width: `${Math.min(c.pct_of_revenue, 100)}%` }} />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                      Revenue concentration risk: if any single customer exceeds 30-50% of revenue, consider diversification.
                    </div>
                  </div>
                )}
              </div>

              {/* 9. Monthly P&L Summary */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['pl'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('pl')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>Monthly P&L Summary</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {formatCurrency(yearEndData.monthlyPL.reduce((s, m) => s + m.net_income, 0))} annual net income
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['pl'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['pl'] && (
                  <div style={{ padding: 20 }}>
                    <div className="table-wrap">
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <SortHeader section="pl" field="month_num">Month</SortHeader>
                            <SortHeader section="pl" field="invoiced" style={{ textAlign: 'right' }}>Invoiced</SortHeader>
                            <SortHeader section="pl" field="collected" style={{ textAlign: 'right' }}>Collected</SortHeader>
                            <SortHeader section="pl" field="contractor_payments" style={{ textAlign: 'right' }}>Contractor Pay</SortHeader>
                            <SortHeader section="pl" field="net_income" style={{ textAlign: 'right' }}>Net Income</SortHeader>
                          </tr>
                        </thead>
                        <tbody>
                          {sortYearEndData(yearEndData.monthlyPL, 'pl').map(m => (
                            <tr key={m.month_num}>
                              <td><strong>{m.month}</strong></td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(m.invoiced)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(m.collected)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#dc2626' }}>{formatCurrency(m.contractor_payments)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600, color: m.net_income >= 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(m.net_income)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                            <td>Annual Total</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.monthlyPL.reduce((s, m) => s + m.invoiced, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(yearEndData.monthlyPL.reduce((s, m) => s + m.collected, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: '#dc2626' }}>{formatCurrency(yearEndData.monthlyPL.reduce((s, m) => s + m.contractor_payments, 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{formatCurrency(yearEndData.monthlyPL.reduce((s, m) => s + m.net_income, 0))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* 10. Open Projects Carryover */}
              <div className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: yearEndExpanded['carryover'] ? '1px solid var(--border)' : 'none' }}
                  onClick={() => toggleYearEndSection('carryover')}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>Open Projects Carryover</span>
                    <span style={{ fontSize: 12, color: '#64748b', marginLeft: 12 }}>
                      {yearEndData.openProjects.length} active projects carrying into next year
                    </span>
                  </div>
                  <span style={{ fontSize: 18, color: '#94a3b8' }}>{yearEndExpanded['carryover'] ? '▾' : '▸'}</span>
                </div>
                {yearEndExpanded['carryover'] && (
                  <div style={{ padding: 20 }}>
                    {yearEndData.openProjects.length === 0 ? (
                      <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>No active projects.</div>
                    ) : (
                      <div className="table-wrap">
                        <table style={{ fontSize: 13 }}>
                          <thead>
                            <tr>
                              <SortHeader section="carryover" field="project_name">Project</SortHeader>
                              <SortHeader section="carryover" field="customer_name">Customer</SortHeader>
                              <SortHeader section="carryover" field="project_type">Type</SortHeader>
                              <th>PO #</th>
                              <SortHeader section="carryover" field="po_amount" style={{ textAlign: 'right' }}>PO Amount</SortHeader>
                              <SortHeader section="carryover" field="amount_billed" style={{ textAlign: 'right' }}>Billed</SortHeader>
                              <SortHeader section="carryover" field="remaining_budget" style={{ textAlign: 'right' }}>Remaining Budget</SortHeader>
                              <SortHeader section="carryover" field="uncollected" style={{ textAlign: 'right' }}>Uncollected</SortHeader>
                            </tr>
                          </thead>
                          <tbody>
                            {sortYearEndData(yearEndData.openProjects, 'carryover').map(p => (
                              <tr key={p.id}>
                                <td><strong>{p.project_name}</strong></td>
                                <td>{p.customer_name}</td>
                                <td style={{ fontSize: 11 }}>
                                  <span className={`badge ${p.project_type === 'fixed_price' || p.project_type === 'piece_rate' ? 'badge-fixed' : 'badge-hourly'}`}>{p.project_type === 'piece_rate' ? 'Piece Rate' : p.project_type === 'fixed_price' ? 'Fixed' : p.project_type === 'fixed_monthly' ? 'Monthly' : 'Hourly'}</span>
                                </td>
                                <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{p.po_number || '—'}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(p.po_amount)}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(p.amount_billed)}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 600, color: p.remaining_budget < 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(p.remaining_budget)}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: p.uncollected > 0 ? '#f59e0b' : '#94a3b8' }}>{formatCurrency(p.uncollected)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: 'var(--surface2)', fontWeight: 600 }}>
                              <td colSpan={4}>Total</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.openProjects.reduce((s, p) => s + (p.po_amount || 0), 0))}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.openProjects.reduce((s, p) => s + p.amount_billed, 0))}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.openProjects.reduce((s, p) => s + p.remaining_budget, 0))}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(yearEndData.openProjects.reduce((s, p) => s + p.uncollected, 0))}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Print All Button */}
              <div className="no-print" style={{ textAlign: 'center', padding: 16 }}>
                <button className="btn btn-secondary" onClick={() => {
                  setYearEndExpanded({ '1099': true, revenue: true, ar: true, quarterly: true, profit: true, utilization: true, aged: true, concentration: true, pl: true, carryover: true });
                  setTimeout(() => window.print(), 100);
                }}>
                  Print All Reports
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'overdue' && (
        <div>
          {loading ? <div style={{ padding: 20, color: '#94a3b8' }}>Loading...</div> : (() => {
            const overdue = overdueData.filter(inv => inv.days_overdue > 0);
            const current = overdueData.filter(inv => inv.days_overdue <= 0);
            const agingBuckets = {
              '1-30': overdue.filter(inv => inv.aging === '1-30'),
              '31-60': overdue.filter(inv => inv.aging === '31-60'),
              '61-90': overdue.filter(inv => inv.aging === '61-90'),
              '90+': overdue.filter(inv => inv.aging === '90+'),
            };
            const totalOverdue = overdue.reduce((s, inv) => s + inv.balance, 0);
            const totalCurrent = current.reduce((s, inv) => s + inv.balance, 0);

            return (
              <>
                {/* Aging Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
                  <div className="card" style={{ padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Current</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>{formatCurrency(totalCurrent)}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{current.length} invoice{current.length !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="card" style={{ padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>1-30 Days</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{formatCurrency(agingBuckets['1-30'].reduce((s, i) => s + i.balance, 0))}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{agingBuckets['1-30'].length} invoice{agingBuckets['1-30'].length !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="card" style={{ padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>31-60 Days</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#f97316' }}>{formatCurrency(agingBuckets['31-60'].reduce((s, i) => s + i.balance, 0))}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{agingBuckets['31-60'].length} invoice{agingBuckets['31-60'].length !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="card" style={{ padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>61-90 Days</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444' }}>{formatCurrency(agingBuckets['61-90'].reduce((s, i) => s + i.balance, 0))}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{agingBuckets['61-90'].length} invoice{agingBuckets['61-90'].length !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="card" style={{ padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>90+ Days</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#dc2626' }}>{formatCurrency(agingBuckets['90+'].reduce((s, i) => s + i.balance, 0))}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{agingBuckets['90+'].length} invoice{agingBuckets['90+'].length !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="card" style={{ padding: 16, textAlign: 'center', background: totalOverdue > 0 ? '#fef2f2' : undefined }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Total Overdue</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: totalOverdue > 0 ? '#dc2626' : '#10b981' }}>{formatCurrency(totalOverdue)}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{overdue.length} invoice{overdue.length !== 1 ? 's' : ''}</div>
                  </div>
                </div>

                <PastDueStatements overdueInvoices={overdue} />

                {/* Overdue Invoice Table */}
                {overdue.length === 0 ? (
                  <div className="card" style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                    No overdue invoices. All invoices are current.
                  </div>
                ) : (
                  <div className="card" style={{ padding: 0 }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>
                      Overdue Invoices ({overdue.length})
                    </div>
                    <div className="table-wrap">
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Invoice #</th>
                            <th>Customer</th>
                            <th>Project</th>
                            <th>Invoice Date</th>
                            <th>Due Date</th>
                            <th style={{ textAlign: 'center' }}>Days Overdue</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Paid</th>
                            <th style={{ textAlign: 'right' }}>Balance Due</th>
                            <th>Aging</th>
                          </tr>
                        </thead>
                        <tbody>
                          {overdue.sort((a, b) => b.days_overdue - a.days_overdue).map(inv => (
                            <tr key={inv.id}>
                              <td style={{ fontFamily: 'DM Mono, monospace' }}>{inv.invoice_number}</td>
                              <td>{inv.customer_name}</td>
                              <td style={{ fontSize: 12 }}>{inv.project_name}</td>
                              <td>{formatDate(inv.created_at)}</td>
                              <td>{formatDate(inv.due_date)}</td>
                              <td style={{ textAlign: 'center', fontWeight: 600, color: inv.days_overdue > 90 ? '#dc2626' : inv.days_overdue > 60 ? '#ef4444' : inv.days_overdue > 30 ? '#f97316' : '#f59e0b' }}>
                                {inv.days_overdue}
                              </td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(inv.total_amount)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(inv.amount_paid)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'DM Mono, monospace' }}>{formatCurrency(inv.balance)}</td>
                              <td>
                                <span style={{
                                  padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                                  background: inv.aging === '90+' ? '#fecaca' : inv.aging === '61-90' ? '#fed7aa' : inv.aging === '31-60' ? '#fef08a' : '#fef9c3',
                                  color: inv.aging === '90+' ? '#991b1b' : inv.aging === '61-90' ? '#9a3412' : inv.aging === '31-60' ? '#854d0e' : '#a16207',
                                }}>
                                  {inv.aging} days
                                </span>
                              </td>
                            </tr>
                          ))}
                          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                            <td colSpan={6}>Totals</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(overdue.reduce((s, i) => s + (i.total_amount || 0), 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(overdue.reduce((s, i) => s + (i.amount_paid || 0), 0))}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(totalOverdue)}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Current (Not Yet Due) */}
                {current.length > 0 && (
                  <div className="card" style={{ padding: 0, marginTop: 20 }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>
                      Current - Not Yet Due ({current.length})
                    </div>
                    <div className="table-wrap">
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Invoice #</th>
                            <th>Customer</th>
                            <th>Project</th>
                            <th>Invoice Date</th>
                            <th>Due Date</th>
                            <th style={{ textAlign: 'center' }}>Days Until Due</th>
                            <th style={{ textAlign: 'right' }}>Balance Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {current.sort((a, b) => a.days_overdue - b.days_overdue).map(inv => (
                            <tr key={inv.id}>
                              <td style={{ fontFamily: 'DM Mono, monospace' }}>{inv.invoice_number}</td>
                              <td>{inv.customer_name}</td>
                              <td style={{ fontSize: 12 }}>{inv.project_name}</td>
                              <td>{formatDate(inv.created_at)}</td>
                              <td>{formatDate(inv.due_date)}</td>
                              <td style={{ textAlign: 'center', color: '#10b981' }}>{Math.abs(inv.days_overdue)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(inv.balance)}</td>
                            </tr>
                          ))}
                          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                            <td colSpan={6}>Total</td>
                            <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{formatCurrency(totalCurrent)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {activeTab === 'po-history' && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>PO History</h3>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
            <div>
              <label className="form-label">Engineer Assigned</label>
              <select
                className="form-select"
                value={poHistoryEngineerId}
                onChange={(e) => {
                  const engineerId = e.target.value;
                  setPoHistoryEngineerId(engineerId);
                  const selectedProject = poHistoryProjects.find(p => String(p.id) === String(poHistoryProjectId));
                  if (engineerId && selectedProject && !selectedProject.engineers.some(eng => String(eng.id) === engineerId)) {
                    setPoHistoryProjectId('');
                  }
                }}
                style={{ minWidth: 200 }}
              >
                <option value="">All Engineers</option>
                {getPoHistoryEngineers(poHistoryProjects).map(eng => (
                  <option key={eng.id} value={eng.id}>{eng.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Select Project / PO</label>
              <select
                className="form-select"
                value={poHistoryProjectId}
                onChange={(e) => setPoHistoryProjectId(e.target.value)}
                style={{ minWidth: 350 }}
              >
                <option value="">-- Select a project --</option>
                {poHistoryProjects
                  .filter(p => !poHistoryEngineerId || p.engineers.some(eng => String(eng.id) === poHistoryEngineerId))
                  .map(p => (
                  <option key={p.id} value={p.id}>{p.customer_name} — {p.name}{p.po_number ? ` (PO: ${p.po_number})` : ''}</option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-primary"
              disabled={!poHistoryProjectId || poHistoryLoading}
              onClick={async () => {
                setPoHistoryLoading(true);
                try {
                  const data = await apiFetch(`/reports/po-history?project_id=${poHistoryProjectId}`);
                  setPoHistoryData(data);
                } catch (err) {
                  setError(err.message);
                }
                setPoHistoryLoading(false);
              }}
            >
              {poHistoryLoading ? 'Loading...' : 'Load History'}
            </button>
          </div>

          {poHistoryData && (
            <>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Project</div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{poHistoryData.project.name}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Customer</div>
                    <div style={{ fontWeight: 500 }}>{poHistoryData.project.customer_name}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>PO Number</div>
                    <div style={{ fontWeight: 500 }}>{poHistoryData.project.po_number || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Type</div>
                    <div><span className={`badge ${poHistoryData.project.project_type === 'fixed_price' || poHistoryData.project.project_type === 'piece_rate' ? 'badge-fixed' : poHistoryData.project.project_type === 'fixed_monthly' ? 'badge-submitted' : 'badge-hourly'}`}>{poHistoryData.project.project_type === 'piece_rate' ? 'Piece Rate' : poHistoryData.project.project_type === 'fixed_price' ? 'Fixed Price' : poHistoryData.project.project_type === 'fixed_monthly' ? 'Fixed Monthly' : 'Hourly'}</span></div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Status</div>
                    <div><span className={`badge badge-${poHistoryData.project.status}`}>{poHistoryData.project.status}</span></div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>PO Budget</div>
                    <div style={{ fontWeight: 700, fontSize: 18, fontFamily: 'DM Mono, monospace' }}>{poHistoryData.project.budget > 0 ? formatCurrency(poHistoryData.project.budget) : '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Total Invoiced</div>
                    <div style={{ fontWeight: 700, fontSize: 18, fontFamily: 'DM Mono, monospace', color: '#2563eb' }}>{formatCurrency(poHistoryData.project.total_invoiced)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Total Paid</div>
                    <div style={{ fontWeight: 700, fontSize: 18, fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(poHistoryData.project.total_paid)}</div>
                  </div>
                  {poHistoryData.project.remaining !== null && (
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Remaining</div>
                      <div style={{ fontWeight: 700, fontSize: 18, fontFamily: 'DM Mono, monospace', color: poHistoryData.project.remaining < 0 ? '#dc2626' : '#64748b' }}>{formatCurrency(poHistoryData.project.remaining)}</div>
                    </div>
                  )}
                </div>
                {poHistoryData.project.budget > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ background: '#e2e8f0', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                      <div style={{ background: poHistoryData.project.total_invoiced > poHistoryData.project.budget ? '#dc2626' : '#2563eb', height: '100%', width: `${Math.min(100, (poHistoryData.project.total_invoiced / poHistoryData.project.budget) * 100)}%`, borderRadius: 4, transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                      {((poHistoryData.project.total_invoiced / poHistoryData.project.budget) * 100).toFixed(1)}% of budget used
                    </div>
                  </div>
                )}
              </div>

              {poHistoryData.engineers.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ marginBottom: 8, fontSize: 14 }}>Assigned Engineers</h4>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {poHistoryData.engineers.map(eng => (
                      <span key={eng.id} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>
                        {eng.engineer_name}
                        {eng.bill_rate > 0 && <span style={{ color: '#64748b', marginLeft: 4 }}>@ {formatCurrency(eng.bill_rate)}/hr</span>}
                        {eng.ot_bill_rate > 0 && <span style={{ color: '#7c3aed', marginLeft: 4 }}>OT: {formatCurrency(eng.ot_bill_rate)}/hr</span>}
                        {eng.monthly_bill > 0 && <span style={{ color: '#64748b', marginLeft: 4 }}>{formatCurrency(eng.monthly_bill)}/mo</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <h4 style={{ marginBottom: 8, fontSize: 14 }}>Invoices ({poHistoryData.invoices.length})</h4>
              {poHistoryData.invoices.length === 0 ? (
                <div style={{ padding: 20, color: '#94a3b8', textAlign: 'center' }}>No invoices generated for this project yet.</div>
              ) : (
                <div className="table-wrap" style={{ marginBottom: 20 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Period</th>
                        <th>Hours</th>
                        <th>Amount</th>
                        <th>Paid</th>
                        <th>Balance</th>
                        <th>Status</th>
                        <th>Date Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poHistoryData.invoices.map(inv => {
                        const balance = (inv.total_amount || 0) - (inv.amount_paid || 0);
                        return (
                          <tr key={inv.id}>
                            <td style={{ fontWeight: 600 }}>{inv.invoice_number}</td>
                            <td style={{ fontSize: 12 }}>{formatDate(inv.period_start)} — {formatDate(inv.period_end)}</td>
                            <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{(inv.total_hours || 0).toFixed(2)}</td>
                            <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{formatCurrency(inv.total_amount)}</td>
                            <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: '#16a34a' }}>{formatCurrency(inv.amount_paid)}</td>
                            <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: balance > 0.01 ? '#dc2626' : '#16a34a' }}>{formatCurrency(balance)}</td>
                            <td><span className={`badge badge-${inv.status}`}>{inv.status}</span></td>
                            <td style={{ fontSize: 12 }}>{formatDate(inv.created_at)}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                        <td colSpan={2}>Totals</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{poHistoryData.invoices.reduce((s, i) => s + (i.total_hours || 0), 0).toFixed(2)}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{formatCurrency(poHistoryData.invoices.reduce((s, i) => s + (i.total_amount || 0), 0))}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: '#16a34a' }}>{formatCurrency(poHistoryData.invoices.reduce((s, i) => s + (i.amount_paid || 0), 0))}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{formatCurrency(poHistoryData.invoices.reduce((s, i) => s + ((i.total_amount || 0) - (i.amount_paid || 0)), 0))}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {poHistoryData.payments.length > 0 && (
                <>
                  <h4 style={{ marginBottom: 8, fontSize: 14 }}>Payments ({poHistoryData.payments.length})</h4>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Invoice #</th>
                          <th>Amount</th>
                          <th>Method</th>
                          <th>Reference</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {poHistoryData.payments.map(pmt => (
                          <tr key={pmt.id}>
                            <td>{formatDate(pmt.payment_date)}</td>
                            <td style={{ fontWeight: 500 }}>{pmt.invoice_number}</td>
                            <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: '#16a34a' }}>{formatCurrency(pmt.amount)}</td>
                            <td>{pmt.payment_method || '—'}</td>
                            <td style={{ fontSize: 12 }}>{pmt.reference_number || '—'}</td>
                            <td style={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pmt.notes || '—'}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                          <td>Total</td>
                          <td></td>
                          <td style={{ fontFamily: 'DM Mono, monospace', textAlign: 'right', color: '#16a34a' }}>{formatCurrency(poHistoryData.payments.reduce((s, p) => s + (p.amount || 0), 0))}</td>
                          <td colSpan={3}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
